// Property detail page logic
import { apiGet } from './api.js';
import { formatNumber, formatCurrency, formatSqft, formatFeet, formatDimensions, formatDate, formatBBL, boroughName, landUseLabel } from './utils.js';
import { initMap, setMarker, flyTo, getMap } from './map.js';
import { calcMonthlyMortgage, estimatePriceFromProperty } from './mortgage-utils.js';
import { getCaliforniaDiligenceSnapshot, getCaliforniaRecordTrail, getCaliforniaOfferReadiness, getCaliforniaEscrowChecklist, getCaliforniaRiskTriage } from './ca-diligence.js';
import { getRecordCountActionPlan } from './property-action-plan.js';

let propertyData = null;
let zoningData = null;
let devData = null;
let detailData = null;
let userTier = 'free';

// Track which extra data has been loaded
let salesLoaded = false;
let ecbLoaded = false;
let hpdRegLoaded = false;
let taxHistoryLoaded = false;
let dobJobsLoaded = false;
let cofOccLoaded = false;
let complaintsLoaded = false;
let eDesigLoaded = false;
let llcPortfolioLoaded = false;
let taxBalanceLoaded = false;
let healthScoreLoaded = false;
let healthDataLoaded = false;
let buildingProfileLoaded = false;
let buildingProfileData = null;

// ─── Sortable + Filterable Table Utility ───

let tableCounter = 0;

function buildTable(containerId, { title, headers, rows, defaultSort }) {
  const el = typeof containerId === 'string' ? document.getElementById(containerId) : containerId;
  if (!el) return '';

  const tableId = `st-${++tableCounter}`;
  const sortCol = defaultSort?.col ?? 0;
  const sortDir = defaultSort?.dir ?? 'desc';

  // Store row data for sorting/filtering
  const storeKey = `__tableData_${tableId}`;
  window[storeKey] = { headers, rows, originalRows: [...rows] };

  let html = `<div class="data-table-wrap" id="${tableId}-wrap">`;
  if (rows.length > 3) {
    html += `<div class="table-controls">
      <input type="text" class="table-filter" placeholder="Filter rows..." data-table="${tableId}">
      <button class="btn-reset-sort" data-table="${tableId}" title="Reset sort to default">Reset sort</button>
      <span class="table-count" data-table="${tableId}-count">${rows.length} rows</span>
    </div>`;
  }
  html += `<table class="data-table" id="${tableId}">
    <thead><tr>${headers.map((h, i) =>
      `<th data-col="${i}" data-table="${tableId}"><span>${h}</span><span class="sort-icon"></span></th>`
    ).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>
  </table></div>`;

  // Return html string if containerId is not a string (caller will set innerHTML)
  if (typeof containerId !== 'string') return html;

  // Otherwise inject and wire up
  el.innerHTML = (title ? `<h3 class="section-header">${title}</h3>` : '') + html;
  wireTable(tableId, sortCol, sortDir);
}

function wireTable(tableId, defaultSortCol, defaultSortDir) {
  setTimeout(() => {
    const table = document.getElementById(tableId);
    if (!table) return;

    const storeKey = `__tableData_${tableId}`;
    const store = window[storeKey];
    if (!store) return;

    let currentSortCol = -1;
    let currentSortDir = null;

    // Sort headers
    table.querySelectorAll('th').forEach(th => {
      th.addEventListener('click', () => {
        const col = parseInt(th.dataset.col);
        if (currentSortCol === col) {
          currentSortDir = currentSortDir === 'asc' ? 'desc' : 'asc';
        } else {
          currentSortCol = col;
          currentSortDir = 'desc';
        }
        applySort(tableId, col, currentSortDir);
        // show reset button
        const resetBtn = document.querySelector(`.btn-reset-sort[data-table="${tableId}"]`);
        if (resetBtn) resetBtn.classList.add('visible');
      });
    });

    // Filter input
    const filterInput = document.querySelector(`.table-filter[data-table="${tableId}"]`);
    if (filterInput) {
      filterInput.addEventListener('input', () => applyFilter(tableId, filterInput.value));
    }

    // Reset button
    const resetBtn = document.querySelector(`.btn-reset-sort[data-table="${tableId}"]`);
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        currentSortCol = -1;
        currentSortDir = null;
        // Restore original order
        const tbody = table.querySelector('tbody');
        const origRows = store.originalRows;
        tbody.innerHTML = origRows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('');
        table.querySelectorAll('th').forEach(t => { t.classList.remove('sort-asc', 'sort-desc'); });
        resetBtn.classList.remove('visible');
        if (filterInput) { filterInput.value = ''; applyFilter(tableId, ''); }
      });
    }

    // Apply default sort
    if (defaultSortCol >= 0) {
      applySort(tableId, defaultSortCol, defaultSortDir);
    }
  }, 0);
}

function applySort(tableId, col, dir) {
  const table = document.getElementById(tableId);
  if (!table) return;

  const tbody = table.querySelector('tbody');
  const rowEls = Array.from(tbody.querySelectorAll('tr'));

  rowEls.sort((a, b) => {
    const aText = (a.children[col]?.textContent || '').trim();
    const bText = (b.children[col]?.textContent || '').trim();

    // Try numeric comparison (strip $ , and other chars)
    const aNum = parseFloat(aText.replace(/[$,%]/g, '').replace(/,/g, ''));
    const bNum = parseFloat(bText.replace(/[$,%]/g, '').replace(/,/g, ''));
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return dir === 'asc' ? aNum - bNum : bNum - aNum;
    }

    // Date comparison
    const aDate = Date.parse(aText);
    const bDate = Date.parse(bText);
    if (!isNaN(aDate) && !isNaN(bDate)) {
      return dir === 'asc' ? aDate - bDate : bDate - aDate;
    }

    // String comparison
    return dir === 'asc' ? aText.localeCompare(bText) : bText.localeCompare(aText);
  });

  rowEls.forEach(r => tbody.appendChild(r));

  // Update header indicators
  table.querySelectorAll('th').forEach(th => {
    th.classList.remove('sort-asc', 'sort-desc');
    if (parseInt(th.dataset.col) === col) {
      th.classList.add(dir === 'asc' ? 'sort-asc' : 'sort-desc');
    }
  });
}

function applyFilter(tableId, text) {
  const table = document.getElementById(tableId);
  if (!table) return;

  const filter = text.toLowerCase().trim();
  const rows = table.querySelectorAll('tbody tr');
  let visible = 0;

  rows.forEach(row => {
    if (!filter) {
      row.classList.remove('filtered-out');
      visible++;
    } else {
      const match = Array.from(row.children).some(td => td.textContent.toLowerCase().includes(filter));
      row.classList.toggle('filtered-out', !match);
      if (match) visible++;
    }
  });

  const countEl = document.querySelector(`[data-table="${tableId}-count"]`);
  if (countEl) countEl.textContent = filter ? `${visible} of ${rows.length} rows` : `${rows.length} rows`;
}

// Helper to generate table HTML string (for sections built with innerHTML concatenation)
function tableHtml(headers, rows, defaultSort) {
  const tableId = `st-${++tableCounter}`;
  const storeKey = `__tableData_${tableId}`;
  window[storeKey] = { headers, rows, originalRows: [...rows] };

  let html = `<div class="data-table-wrap" id="${tableId}-wrap">`;
  if (rows.length > 3) {
    html += `<div class="table-controls">
      <input type="text" class="table-filter" placeholder="Filter rows..." data-table="${tableId}">
      <button class="btn-reset-sort" data-table="${tableId}" title="Reset sort to default">Reset sort</button>
      <span class="table-count" data-table="${tableId}-count">${rows.length} rows</span>
    </div>`;
  }
  html += `<table class="data-table" id="${tableId}">
    <thead><tr>${headers.map((h, i) =>
      `<th data-col="${i}" data-table="${tableId}"><span>${h}</span><span class="sort-icon"></span></th>`
    ).join('')}</tr></thead>
    <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>
  </table></div>`;

  // Schedule wiring after DOM update
  setTimeout(() => wireTable(tableId, defaultSort?.col ?? -1, defaultSort?.dir ?? 'desc'), 0);
  return html;
}

// ─── Building Profile Diagrams ───

async function loadBuildingProfile() {
  if (buildingProfileLoaded) return;
  buildingProfileLoaded = true;

  const container = document.getElementById('building-profile');
  if (!container || !propertyData?.bbl) return;

  container.innerHTML = '<div class="loading">Loading building profile...</div>';

  try {
    buildingProfileData = await apiGet(`/api/building-profile?bbl=${propertyData.bbl}`);
    renderBuildingProfile(container, buildingProfileData);
    // If map is already initialized, add footprint
    if (buildingProfileData.footprint) {
      const m = getMap();
      if (m) addFootprintToMap(m, buildingProfileData.footprint);
    }
  } catch (e) {
    container.innerHTML = '';
  }
}

function renderBuildingProfile(container, data) {
  const { lot, building, use, zoning, footprint } = data;

  // Need at least some dimensions to render
  if (!lot.front && !lot.depth && !building.front && !building.depth && !building.floors) {
    container.innerHTML = '';
    return;
  }

  let html = '<div class="bp-section">';
  html += '<h3 class="section-header">Building Profile</h3>';
  html += '<div class="bp-disclaimer">Estimated from public records (PLUTO, NYC Building Footprints, Zoning). Not an architectural floor plan.</div>';
  html += '<div class="bp-diagrams">';

  // Diagram 1: Lot Site Plan (bird's eye view)
  if (lot.front && lot.depth) {
    html += renderSitePlanSVG(lot, building, zoning);
  }

  // Diagram 2: Building Cross-Section (side elevation)
  if (building.floors && building.floors > 0) {
    html += renderCrossSectionSVG(building, use);
  }

  html += '</div>';

  // Diagram 3: Zoning Ghost Volume (existing vs max envelope) — Pro only
  if (building.floors && building.floors > 0 && devData) {
    const maxFAR = devData.official_res_far;
    const builtFAR = devData.built_far;
    const maxBuildable = devData.max_buildable_sqft;
    const unusedDev = devData.unused_dev_rights;
    const maxHeight = devData.max_height;

    if (maxFAR && builtFAR && lot.area) {
      html += renderGhostVolumeSVG(lot, building, zoning, {
        maxFAR, builtFAR, maxBuildable, unusedDev, maxHeight,
      });
    }
  }

  html += '</div>';
  container.innerHTML = html;
}

function renderSitePlanSVG(lot, building, zoning) {
  const PAD = 40;
  const MAX_W = 260;
  const MAX_H = 260;

  const lotW = lot.front;
  const lotD = lot.depth;
  const bldgW = building.front || lotW * 0.8;
  const bldgD = building.depth || lotD * 0.6;

  // Scale to fit
  const scale = Math.min(MAX_W / lotW, MAX_H / lotD);
  const sLotW = Math.round(lotW * scale);
  const sLotD = Math.round(lotD * scale);
  const sBldgW = Math.round(bldgW * scale);
  const sBldgD = Math.round(bldgD * scale);

  const svgW = sLotW + PAD * 2;
  const svgH = sLotD + PAD * 2 + 20; // extra for bottom labels

  // Zoning setback offsets
  const frontYard = zoning.front_yard_min ? Math.round(zoning.front_yard_min * scale) : 0;
  const rearYard = zoning.rear_yard_min ? Math.round(zoning.rear_yard_min * scale) : 0;

  // Position building — centered horizontally, offset from front by setback (or centered if no setback)
  const bldgX = PAD + Math.round((sLotW - sBldgW) / 2);
  const bldgY = PAD + (frontYard || Math.round((sLotD - sBldgD) / 2));

  // Lot coverage
  const lotArea = lot.area || lotW * lotD;
  const bldgFootprint = bldgW * bldgD;
  const coveragePct = lotArea > 0 ? Math.round((bldgFootprint / lotArea) * 100) : null;

  let svg = `<div class="bp-diagram"><h4>Lot Site Plan (Top-Down)</h4>`;
  svg += `<svg viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" xmlns="http://www.w3.org/2000/svg">`;

  // Lot boundary
  svg += `<rect x="${PAD}" y="${PAD}" width="${sLotW}" height="${sLotD}" fill="#f1f5f9" stroke="#94a3b8" stroke-width="2" stroke-dasharray="6,3"/>`;

  // Zoning setback lines
  if (frontYard > 0) {
    const y = PAD + frontYard;
    svg += `<line x1="${PAD}" y1="${y}" x2="${PAD + sLotW}" y2="${y}" stroke="#ca8a04" stroke-width="1" stroke-dasharray="4,4"/>`;
    svg += `<text x="${PAD + sLotW + 3}" y="${PAD + frontYard / 2 + 4}" fill="#ca8a04" font-size="9" font-family="Inter,sans-serif">${zoning.front_yard_min}ft</text>`;
  }
  if (rearYard > 0) {
    const y = PAD + sLotD - rearYard;
    svg += `<line x1="${PAD}" y1="${y}" x2="${PAD + sLotW}" y2="${y}" stroke="#ca8a04" stroke-width="1" stroke-dasharray="4,4"/>`;
    svg += `<text x="${PAD + sLotW + 3}" y="${y + 4}" fill="#ca8a04" font-size="9" font-family="Inter,sans-serif">${zoning.rear_yard_min}ft</text>`;
  }

  // Building footprint
  svg += `<rect x="${bldgX}" y="${bldgY}" width="${sBldgW}" height="${sBldgD}" fill="#dbeafe" stroke="#1a56db" stroke-width="2" rx="2"/>`;

  // Lot dimension labels
  // Top: lot front
  svg += `<text x="${PAD + sLotW / 2}" y="${PAD - 8}" text-anchor="middle" fill="#64748b" font-size="11" font-family="Inter,sans-serif" font-weight="600">${lotW}ft</text>`;
  // Left: lot depth
  svg += `<text x="${PAD - 8}" y="${PAD + sLotD / 2}" text-anchor="middle" fill="#64748b" font-size="11" font-family="Inter,sans-serif" font-weight="600" transform="rotate(-90 ${PAD - 8} ${PAD + sLotD / 2})">${lotD}ft</text>`;

  // Building dimension labels (inside)
  if (building.front) {
    svg += `<text x="${bldgX + sBldgW / 2}" y="${bldgY + sBldgD / 2 - 6}" text-anchor="middle" fill="#1a56db" font-size="10" font-family="Inter,sans-serif" font-weight="600">${building.front}ft</text>`;
  }
  if (building.depth) {
    svg += `<text x="${bldgX + sBldgW / 2}" y="${bldgY + sBldgD / 2 + 10}" text-anchor="middle" fill="#1a56db" font-size="10" font-family="Inter,sans-serif">× ${building.depth}ft</text>`;
  }

  // Coverage label at bottom
  if (coveragePct !== null) {
    svg += `<text x="${svgW / 2}" y="${svgH - 4}" text-anchor="middle" fill="#64748b" font-size="10" font-family="Inter,sans-serif">Lot coverage: ${coveragePct}%</text>`;
  }

  // Legend
  svg += `<rect x="${PAD}" y="${svgH - 18}" width="8" height="8" fill="#f1f5f9" stroke="#94a3b8" stroke-width="1"/>`;
  svg += `<text x="${PAD + 12}" y="${svgH - 11}" fill="#64748b" font-size="9" font-family="Inter,sans-serif">Lot</text>`;
  svg += `<rect x="${PAD + 38}" y="${svgH - 18}" width="8" height="8" fill="#dbeafe" stroke="#1a56db" stroke-width="1"/>`;
  svg += `<text x="${PAD + 50}" y="${svgH - 11}" fill="#64748b" font-size="9" font-family="Inter,sans-serif">Building</text>`;

  svg += `</svg></div>`;
  return svg;
}

function renderCrossSectionSVG(building, use) {
  const PAD = 40;
  const MAX_W = 240;
  const MAX_H = 280;
  const floors = Math.min(building.floors, 60); // cap for rendering

  // Determine height per floor
  const totalHeight = building.height_roof || floors * 10;
  const floorHeight = totalHeight / floors;

  // Scale
  const bldgWidth = building.front || 40;
  const scaleX = Math.min(MAX_W / bldgWidth, 3);
  const scaleY = Math.min(MAX_H / totalHeight, MAX_H / (floors * 12));
  const sFloorH = Math.max(Math.round(floorHeight * scaleY), 8);
  const sBldgW = Math.round(bldgWidth * scaleX);
  const sBldgH = sFloorH * floors;

  const svgW = sBldgW + PAD * 2;
  const svgH = sBldgH + PAD * 2 + 10;

  // Determine commercial vs residential floors
  const totalArea = (use.res_area || 0) + (use.com_area || 0);
  let comFloors = 0;
  if (totalArea > 0 && use.com_area > 0) {
    comFloors = Math.max(1, Math.round(floors * (use.com_area / totalArea)));
  }
  const resFloors = floors - comFloors;

  // Area per floor (approximate)
  const areaPerFloor = building.area ? Math.round(building.area / floors) : null;

  let svg = `<div class="bp-diagram"><h4>Cross-Section (Side Elevation)</h4>`;
  svg += `<svg viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" xmlns="http://www.w3.org/2000/svg">`;

  // Ground line
  const groundY = PAD + sBldgH;
  svg += `<line x1="${PAD - 10}" y1="${groundY}" x2="${PAD + sBldgW + 10}" y2="${groundY}" stroke="#64748b" stroke-width="2"/>`;

  // Draw floors bottom-up
  for (let i = 0; i < floors; i++) {
    const floorNum = i + 1;
    const y = groundY - (i + 1) * sFloorH;
    const isCommercial = floorNum <= comFloors;
    const fill = isCommercial ? '#fed7aa' : '#dbeafe'; // orange for commercial, blue for residential
    const stroke = isCommercial ? '#ea580c' : '#1a56db';

    svg += `<rect x="${PAD}" y="${y}" width="${sBldgW}" height="${sFloorH}" fill="${fill}" stroke="${stroke}" stroke-width="1"/>`;

    // Floor label (only if floor bars are tall enough)
    if (sFloorH >= 14) {
      const label = floors <= 10 ? `F${floorNum}` : (floorNum === 1 || floorNum === floors || floorNum % 5 === 0 ? `${floorNum}` : '');
      if (label) {
        svg += `<text x="${PAD + 6}" y="${y + sFloorH / 2 + 4}" fill="${isCommercial ? '#9a3412' : '#1e40af'}" font-size="${sFloorH >= 18 ? 10 : 8}" font-family="Inter,sans-serif">${label}</text>`;
      }
    }
  }

  // Height label (right side)
  const heightLabel = building.height_roof ? `${Math.round(building.height_roof)}ft` : `~${floors * 10}ft`;
  svg += `<line x1="${PAD + sBldgW + 8}" y1="${PAD}" x2="${PAD + sBldgW + 8}" y2="${groundY}" stroke="#64748b" stroke-width="1"/>`;
  svg += `<line x1="${PAD + sBldgW + 4}" y1="${PAD}" x2="${PAD + sBldgW + 12}" y2="${PAD}" stroke="#64748b" stroke-width="1"/>`;
  svg += `<line x1="${PAD + sBldgW + 4}" y1="${groundY}" x2="${PAD + sBldgW + 12}" y2="${groundY}" stroke="#64748b" stroke-width="1"/>`;
  svg += `<text x="${PAD + sBldgW + 14}" y="${PAD + sBldgH / 2 + 4}" fill="#64748b" font-size="11" font-family="Inter,sans-serif" font-weight="600">${heightLabel}</text>`;

  // Width label (bottom)
  if (building.front) {
    svg += `<text x="${PAD + sBldgW / 2}" y="${groundY + 18}" text-anchor="middle" fill="#64748b" font-size="11" font-family="Inter,sans-serif" font-weight="600">${building.front}ft wide</text>`;
  }

  // Floor count + area label (top)
  let topLabel = `${floors} floors`;
  if (areaPerFloor) topLabel += ` · ~${formatNumber(areaPerFloor)} sqft/floor`;
  svg += `<text x="${PAD + sBldgW / 2}" y="${PAD - 10}" text-anchor="middle" fill="#64748b" font-size="10" font-family="Inter,sans-serif">${topLabel}</text>`;

  // Legend (bottom right)
  const legY = svgH - 8;
  if (comFloors > 0) {
    svg += `<rect x="${PAD}" y="${legY - 10}" width="8" height="8" fill="#dbeafe" stroke="#1a56db" stroke-width="1"/>`;
    svg += `<text x="${PAD + 12}" y="${legY - 3}" fill="#64748b" font-size="9" font-family="Inter,sans-serif">Residential</text>`;
    svg += `<rect x="${PAD + 72}" y="${legY - 10}" width="8" height="8" fill="#fed7aa" stroke="#ea580c" stroke-width="1"/>`;
    svg += `<text x="${PAD + 84}" y="${legY - 3}" fill="#64748b" font-size="9" font-family="Inter,sans-serif">Commercial</text>`;
  }

  svg += `</svg></div>`;
  return svg;
}

function renderGhostVolumeSVG(lot, building, zoning, dev) {
  const PAD = 50;
  const MAX_W = 540;
  const MAX_H = 300;

  const builtFAR = dev.builtFAR || 0;
  const maxFAR = dev.maxFAR || 0;
  const unusedDev = dev.unusedDev || 0;
  const maxBuildable = dev.maxBuildable || 0;
  const isOverbuilt = unusedDev < 0;
  const utilizationPct = maxFAR > 0 ? Math.round((builtFAR / maxFAR) * 100) : 0;

  // Parse max height (e.g., "33 ft" → 33)
  const maxHeightFt = dev.maxHeight ? parseInt(dev.maxHeight) : null;
  const builtHeightFt = building.height_roof || (building.floors * 10);
  const ghostHeightFt = maxHeightFt || (maxFAR / (builtFAR || 1)) * builtHeightFt;

  // Scale
  const bldgWidth = building.front || lot.front || 40;
  const lotWidth = lot.front || bldgWidth;
  const scale = Math.min(MAX_W / (lotWidth * 2.5), MAX_H / Math.max(ghostHeightFt, builtHeightFt));
  const sBuiltH = Math.round(builtHeightFt * scale);
  const sGhostH = Math.round(ghostHeightFt * scale);
  const sBldgW = Math.round(bldgWidth * scale);
  const sLotW = Math.round(lotWidth * scale);

  const svgW = Math.max(sBldgW + PAD * 2 + 160, 360);
  const svgH = Math.max(sGhostH, sBuiltH) + PAD * 2 + 30;
  const groundY = svgH - PAD - 20;

  // Colors
  const builtColor = '#1a56db';
  const ghostColor = isOverbuilt ? '#dc2626' : '#16a34a';
  const ghostFill = isOverbuilt ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)';

  let svg = `<div class="bp-diagram" style="grid-column: 1 / -1">`;
  svg += `<h4>Zoning Ghost Volume — Air Rights Analysis</h4>`;
  svg += `<svg viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" xmlns="http://www.w3.org/2000/svg">`;

  const bldgX = PAD;

  // Ground line
  svg += `<line x1="${PAD - 15}" y1="${groundY}" x2="${PAD + sBldgW + 15}" y2="${groundY}" stroke="#94a3b8" stroke-width="2"/>`;

  // Ghost envelope (max zoning allows) — dashed outline
  if (!isOverbuilt && sGhostH > sBuiltH) {
    svg += `<rect x="${bldgX}" y="${groundY - sGhostH}" width="${sBldgW}" height="${sGhostH}" fill="${ghostFill}" stroke="${ghostColor}" stroke-width="2" stroke-dasharray="6,4" rx="2"/>`;

    // "Available" label in ghost area
    const ghostAreaY = groundY - sGhostH + (sGhostH - sBuiltH) / 2;
    svg += `<text x="${bldgX + sBldgW / 2}" y="${ghostAreaY}" text-anchor="middle" fill="${ghostColor}" font-size="11" font-family="Inter,sans-serif" font-weight="600">AVAILABLE</text>`;
    svg += `<text x="${bldgX + sBldgW / 2}" y="${ghostAreaY + 14}" text-anchor="middle" fill="${ghostColor}" font-size="10" font-family="Inter,sans-serif">${formatNumber(unusedDev)} sqft</text>`;
  }

  // Existing building (solid)
  svg += `<rect x="${bldgX}" y="${groundY - sBuiltH}" width="${sBldgW}" height="${sBuiltH}" fill="#dbeafe" stroke="${builtColor}" stroke-width="2" rx="2"/>`;
  svg += `<text x="${bldgX + sBldgW / 2}" y="${groundY - sBuiltH / 2 - 6}" text-anchor="middle" fill="${builtColor}" font-size="11" font-family="Inter,sans-serif" font-weight="600">EXISTING</text>`;
  svg += `<text x="${bldgX + sBldgW / 2}" y="${groundY - sBuiltH / 2 + 8}" text-anchor="middle" fill="${builtColor}" font-size="10" font-family="Inter,sans-serif">${formatNumber(building.area || 0)} sqft</text>`;

  // Height labels (right side)
  const labelX = bldgX + sBldgW + 12;

  // Built height
  svg += `<line x1="${labelX}" y1="${groundY - sBuiltH}" x2="${labelX}" y2="${groundY}" stroke="${builtColor}" stroke-width="1"/>`;
  svg += `<line x1="${labelX - 4}" y1="${groundY - sBuiltH}" x2="${labelX + 4}" y2="${groundY - sBuiltH}" stroke="${builtColor}" stroke-width="1"/>`;
  svg += `<line x1="${labelX - 4}" y1="${groundY}" x2="${labelX + 4}" y2="${groundY}" stroke="${builtColor}" stroke-width="1"/>`;
  svg += `<text x="${labelX + 8}" y="${groundY - sBuiltH / 2 + 4}" fill="${builtColor}" font-size="10" font-family="Inter,sans-serif">${Math.round(builtHeightFt)}ft</text>`;

  // Ghost height (if taller)
  if (!isOverbuilt && sGhostH > sBuiltH) {
    const lx2 = labelX + 35;
    svg += `<line x1="${lx2}" y1="${groundY - sGhostH}" x2="${lx2}" y2="${groundY}" stroke="${ghostColor}" stroke-width="1" stroke-dasharray="3,3"/>`;
    svg += `<line x1="${lx2 - 4}" y1="${groundY - sGhostH}" x2="${lx2 + 4}" y2="${groundY - sGhostH}" stroke="${ghostColor}" stroke-width="1"/>`;
    svg += `<text x="${lx2 + 8}" y="${groundY - sGhostH / 2 + 4}" fill="${ghostColor}" font-size="10" font-family="Inter,sans-serif">${maxHeightFt ? maxHeightFt + 'ft max' : Math.round(ghostHeightFt) + 'ft'}</text>`;
  }

  // Stats panel (right side)
  const statsX = bldgX + sBldgW + 80;
  const statsY = PAD;
  const statSpacing = 42;

  // Utilization meter
  svg += `<text x="${statsX}" y="${statsY}" fill="#1e293b" font-size="12" font-family="Inter,sans-serif" font-weight="700">Zoning Utilization</text>`;

  // Progress bar
  const barW = 120;
  const barH = 14;
  const barY = statsY + 8;
  const fillW = Math.min(Math.round((utilizationPct / 100) * barW), barW);
  const barColor = utilizationPct > 100 ? '#dc2626' : utilizationPct > 80 ? '#ca8a04' : '#16a34a';
  svg += `<rect x="${statsX}" y="${barY}" width="${barW}" height="${barH}" fill="#e2e8f0" rx="3"/>`;
  svg += `<rect x="${statsX}" y="${barY}" width="${fillW}" height="${barH}" fill="${barColor}" rx="3"/>`;
  svg += `<text x="${statsX + barW + 6}" y="${barY + 11}" fill="${barColor}" font-size="11" font-family="Inter,sans-serif" font-weight="700">${utilizationPct}%</text>`;

  // Stats
  const stats = [
    { label: 'Built FAR', value: builtFAR.toFixed(2) },
    { label: 'Max FAR', value: maxFAR.toFixed(2) },
    { label: 'Max Buildable', value: formatNumber(maxBuildable) + ' sqft' },
    { label: isOverbuilt ? 'Over-built by' : 'Air Rights', value: formatNumber(Math.abs(unusedDev)) + ' sqft' },
  ];

  stats.forEach((s, i) => {
    const y = barY + 30 + i * statSpacing;
    svg += `<text x="${statsX}" y="${y}" fill="#64748b" font-size="10" font-family="Inter,sans-serif">${s.label}</text>`;
    svg += `<text x="${statsX}" y="${y + 16}" fill="#1e293b" font-size="13" font-family="Inter,sans-serif" font-weight="600">${s.value}</text>`;
  });

  // Legend
  const legY = svgH - 12;
  svg += `<rect x="${PAD}" y="${legY - 10}" width="10" height="10" fill="#dbeafe" stroke="${builtColor}" stroke-width="1" rx="1"/>`;
  svg += `<text x="${PAD + 14}" y="${legY}" fill="#64748b" font-size="10" font-family="Inter,sans-serif">Existing Building</text>`;
  if (!isOverbuilt) {
    svg += `<rect x="${PAD + 110}" y="${legY - 10}" width="10" height="10" fill="${ghostFill}" stroke="${ghostColor}" stroke-width="1" stroke-dasharray="3,2" rx="1"/>`;
    svg += `<text x="${PAD + 124}" y="${legY}" fill="#64748b" font-size="10" font-family="Inter,sans-serif">Max Zoning Envelope (Air Rights)</text>`;
  } else {
    svg += `<text x="${PAD + 110}" y="${legY}" fill="#dc2626" font-size="10" font-family="Inter,sans-serif" font-weight="600">Over-built — exceeds current zoning</text>`;
  }

  svg += `</svg></div>`;
  return svg;
}

function addFootprintToMap(map, footprint) {
  if (!map || !footprint) return;

  const sourceId = 'building-footprint';
  if (map.getSource(sourceId)) return;

  map.addSource(sourceId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: footprint,
      properties: {},
    },
  });

  map.addLayer({
    id: 'building-footprint-fill',
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': '#1a56db',
      'fill-opacity': 0.25,
    },
  });

  map.addLayer({
    id: 'building-footprint-outline',
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': '#1a56db',
      'line-width': 2,
    },
  });
}

export async function initPropertyPage() {
  const params = new URLSearchParams(window.location.search);
  const region = params.get('region') || 'nyc';
  const bbl = params.get('bbl');
  const swis = params.get('swis');
  const parcel = params.get('parcel');

  const pamsPin = params.get('pamsPin');
  const address = params.get('address');
  const county = params.get('county');
  const municipality = params.get('municipality');
  const parcelId = params.get('parcelId');
  const caCity = params.get('city');
  const caZip = params.get('zipcode');
  const caLat = params.get('lat');
  const caLng = params.get('lng');
  const opa = params.get('opa');
  const pin = params.get('pin');
  const link = params.get('link');

  if (region === 'nyc' && !bbl) {
    document.getElementById('loading').textContent = 'No BBL specified. Go back to search.';
    return;
  }
  if (region === 'nys' && (!swis || !parcel)) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'nj' && !pamsPin && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'nc' && !parcelId && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'ca' && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'phl' && !opa && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'il' && !pin && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }
  if (region === 'ct' && !link && !address) {
    document.getElementById('loading').textContent = 'No property identifier specified. Go back to search.';
    return;
  }

  // Check user tier
  try {
    const me = await apiGet('/api/auth/me');
    userTier = me.user?.tier || 'free';
  } catch (e) {
    userTier = 'free';
  }

  const loadingEl = document.getElementById('loading');
  loadingEl.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;gap:12px">
      <div style="width:20px;height:20px;border:3px solid var(--border);border-top-color:var(--primary);border-radius:50%;animation:spin 0.8s linear infinite"></div>
      <span>Loading property data...</span>
    </div>
    <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
  `;

  try {
    let apiUrl;
    if (region === 'nj') {
      apiUrl = `/api/property?region=nj&pamsPin=${encodeURIComponent(pamsPin || '')}&address=${encodeURIComponent(address || '')}&county=${encodeURIComponent(county || '')}&municipality=${encodeURIComponent(municipality || '')}`;
    } else if (region === 'nc') {
      apiUrl = `/api/property?region=nc&parcelId=${encodeURIComponent(parcelId || '')}&address=${encodeURIComponent(address || '')}&county=${encodeURIComponent(county || '')}`;
    } else if (region === 'ca') {
      apiUrl = `/api/property?region=ca&address=${encodeURIComponent(address || '')}&city=${encodeURIComponent(caCity || '')}&zipcode=${encodeURIComponent(caZip || '')}&lat=${encodeURIComponent(caLat || '')}&lng=${encodeURIComponent(caLng || '')}`;
    } else if (region === 'phl') {
      apiUrl = `/api/property?region=phl&opa=${encodeURIComponent(opa || '')}&address=${encodeURIComponent(address || '')}`;
    } else if (region === 'il') {
      apiUrl = `/api/property?region=il&pin=${encodeURIComponent(pin || '')}&address=${encodeURIComponent(address || '')}`;
    } else if (region === 'ct') {
      apiUrl = `/api/property?region=ct&link=${encodeURIComponent(link || '')}&address=${encodeURIComponent(address || '')}&city=${encodeURIComponent(caCity || '')}`;
    } else if (region === 'nys') {
      apiUrl = `/api/property?region=nys&swis=${encodeURIComponent(swis)}&parcel=${encodeURIComponent(parcel)}`;
    } else {
      apiUrl = `/api/property?bbl=${bbl}`;
    }
    const data = await apiGet(apiUrl, { timeout: 12000 });
    propertyData = data.property;
    zoningData = data.zoning;
    devData = data.development;
    if (typeof window.trackEvent === 'function') window.trackEvent('property', 'view', String(data.property.bbl || ''));
    renderProperty();
  } catch (e) {
    let message;
    if (e.status === 0) {
      message = 'Could not load property data. The server may be temporarily unavailable.';
    } else if (e.status === 400) {
      message = e.error || 'Invalid BBL format.';
    } else if (e.status === 404) {
      message = 'Property not found in NYC PLUTO database.';
    } else {
      message = e.error || e.message || 'Property not found';
    }
    loadingEl.innerHTML = `
      <div style="max-width:400px;margin:0 auto;text-align:center">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style="margin-bottom:12px"><circle cx="24" cy="24" r="20" stroke="#e2e8f0" stroke-width="2"/><path d="M18 18l12 12M30 18L18 30" stroke="#94a3b8" stroke-width="2" stroke-linecap="round"/></svg>
        <p style="font-size:15px;margin-bottom:16px">${message}</p>
        <a href="/" class="btn btn-primary">Back to search</a>
      </div>`;
  }
}

function renderProperty() {
  const p = propertyData;
  const isNYS = p.region === 'nys';
  const isNJ = p.region === 'nj';
  const isNC = p.region === 'nc';
  const isCA = p.region === 'ca';
  const isPHL = p.region === 'phl';
  const isIL = p.region === 'il';
  const isCT = p.region === 'ct';
  const isOutOfState = isNYS || isNJ || isNC || isCA || isPHL || isIL || isCT;
  document.getElementById('loading').style.display = 'none';
  document.getElementById('property-hero').style.display = 'block';
  document.getElementById('property-content').style.display = 'block';

  // Hero
  let heroAddress = p.address || formatBBL(p.bbl);
  if (isNYS) heroAddress = p.address || 'NYS Property';
  if (isNJ) heroAddress = p.address || 'NJ Property';
  if (isNC) heroAddress = p.address || 'NC Property';
  if (isCA) heroAddress = p.address || 'CA Property';
  if (isPHL) heroAddress = p.address || 'Philadelphia Property';
  if (isIL) heroAddress = p.address || 'IL Property';
  if (isCT) heroAddress = p.address || 'CT Property';
  document.getElementById('prop-address').textContent = heroAddress;

  let heroMeta;
  if (isNJ) {
    heroMeta = `${p.municipality || ''}, ${p.county || ''} County \u00B7 New Jersey${p.zipcode ? ` \u00B7 ${p.zipcode}` : ''}`;
  } else if (isNC) {
    heroMeta = `${p.city || ''}, ${p.county || ''} County \u00B7 North Carolina`;
  } else if (isCA) {
    heroMeta = `${p.city || ''}${p.county ? `, ${p.county} County` : ''} \u00B7 California${p.zipcode ? ` \u00B7 ${p.zipcode}` : ''}`;
  } else if (isNYS) {
    heroMeta = `${p.county || ''} County \u00B7 ${p.town || ''}${p.zipcode ? ` \u00B7 ${p.zipcode}` : ''}`;
  } else if (isPHL) {
    heroMeta = `Philadelphia \u00B7 Pennsylvania${p.opa_account_num ? ` \u00B7 OPA ${p.opa_account_num}` : ''}`;
  } else if (isIL) {
    heroMeta = `${p.city || ''}${p.county ? `, ${p.county} County` : ''} \u00B7 Illinois${p.pin ? ` \u00B7 PIN ${p.pin}` : ''}`;
  } else if (isCT) {
    heroMeta = `${p.city || ''} \u00B7 Connecticut${p.zipcode ? ` \u00B7 ${p.zipcode}` : ''}${p.link ? ` \u00B7 ${p.link}` : ''}`;
  }

  if (isOutOfState) {
    document.getElementById('prop-meta').textContent = heroMeta;
  } else {
    document.getElementById('prop-meta').textContent = `${p.boroughName || boroughName(p.borough)} \u00B7 BBL ${formatBBL(p.bbl)}${p.zipcode ? ` \u00B7 ${p.zipcode}` : ''}`;
  }

  const tags = document.getElementById('prop-tags');
  const tagItems = [];
  if (isNYS) {
    if (p.property_class_desc) tagItems.push(p.property_class_desc);
    tagItems.push('NY State');
  } else if (isCA) {
    if (p.use_type) tagItems.push(p.use_type);
    if (p.county) tagItems.push(`${p.county} County`);
    if (p.year_built) tagItems.push(`Built ${p.year_built}`);
    tagItems.push('California');
  } else if (isPHL) {
    if (p.deed_count) tagItems.push(`${p.deed_count} deed${p.deed_count === 1 ? '' : 's'}`);
    tagItems.push('Philadelphia');
    tagItems.push('Pennsylvania');
  } else if (isIL) {
    if (p.current_use) tagItems.push(p.current_use);
    if (p.county) tagItems.push(`${p.county} County`);
    if (p.transfer_count) tagItems.push(`${p.transfer_count} transfer${p.transfer_count === 1 ? '' : 's'}`);
    tagItems.push('Illinois');
  } else if (isCT) {
    if (p.property_class_desc) tagItems.push(p.property_class_desc);
    if (p.style) tagItems.push(p.style);
    if (p.year_built) tagItems.push(`Built ${p.year_built}`);
    tagItems.push('Connecticut');
  } else {
    if (p.bldg_class) tagItems.push(`Class ${p.bldg_class}`);
    if (p.land_use) tagItems.push(landUseLabel(p.land_use));
    if (p.zoning_dist1) tagItems.push(p.zoning_dist1);
    if (p.year_built) tagItems.push(`Built ${p.year_built}`);
  }
  tags.innerHTML = tagItems.map(t => `<span class="tag">${t}</span>`).join('');

  document.title = `${p.address || (isNYS ? 'NYS Property' : formatBBL(p.bbl))} — PropertyDeepDive`;

  // Update meta tags dynamically
  const metaDesc = document.querySelector('meta[name="description"]');
  if (metaDesc) {
    metaDesc.content = `${p.address || 'Property'} — ${p.boroughName || boroughName(p.borough) || ''} NYC. Zoning: ${p.zoning_dist1 || 'N/A'}. Built: ${p.year_built || 'N/A'}. View ownership, violations, permits, sales history and more.`;
  }
  const ogTitle = document.querySelector('meta[property="og:title"]');
  if (ogTitle) ogTitle.content = document.title;
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.href = window.location.href;
  const ogUrl = document.querySelector('meta[property="og:url"]');
  if (ogUrl) ogUrl.content = window.location.href;

  // Inject JSON-LD structured data for property
  try {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'RealEstateListing',
      'name': p.address || (isNYS ? 'NYS Property' : formatBBL(p.bbl)),
      'url': window.location.href,
      'description': `Property details for ${p.address || 'NYC property'}. ${p.zoning_dist1 ? 'Zoning: ' + p.zoning_dist1 + '.' : ''} ${p.year_built ? 'Built: ' + p.year_built + '.' : ''}`,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': p.address || '',
        'addressLocality': p.boroughName || boroughName(p.borough) || '',
        'addressRegion': 'NY',
        'postalCode': p.zipcode || '',
        'addressCountry': 'US'
      }
    };
    if (p.latitude && p.longitude) {
      jsonLd.geo = { '@type': 'GeoCoordinates', latitude: p.latitude, longitude: p.longitude };
    }
    if (p.year_built) jsonLd.yearBuilt = p.year_built;
    const el = document.getElementById('property-jsonld');
    if (el) el.textContent = JSON.stringify(jsonLd);
  } catch (e) { /* non-critical */ }

  // For NYS/CA/PHL/IL/CT: hide tabs that have no data (zoning, building)
  if (isNYS || isCA || isPHL || isIL || isCT) {
    ['building', 'zoning'].forEach(tab => {
      const btn = document.querySelector(`.tab[data-tab="${tab}"]`);
      if (btn) btn.style.display = 'none';
    });
  }

  // Rent stabilization heuristic (free, computed locally)
  const bldgClass = (p.bldg_class || '').toUpperCase();
  const isMultifamily = bldgClass.startsWith('C') || bldgClass.startsWith('D');
  const preLaw = (p.year_built || 9999) < 1974;
  const sixPlusUnits = (p.units_res || 0) >= 6;
  const likelyStabilized = isMultifamily && preLaw && sixPlusUnits;

  // Use server-computed development data
  const d = devData || {};
  const maxBuildable = d.max_buildable_sqft;
  const unusedDev = d.unused_dev_rights;

  // Pro-gated field helper: only show PRO lock if data exists, otherwise omit entirely
  const proLock = '<a href="/pricing" class="pro-field-lock">PRO</a>';
  function proField(label, value) {
    if (value == null || value === '-' || value === '' || value === 0) return null;
    if (userTier === 'free') return { label, value: proLock };
    return { label, value };
  }

  // Overview tab
  let overviewItems;
  if (isPHL) {
    overviewItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'City', value: 'Philadelphia' },
      { label: 'County', value: 'Philadelphia' },
      { label: 'State', value: 'Pennsylvania' },
      p.opa_account_num ? { label: 'OPA Account #', value: p.opa_account_num } : null,
      p.deed_count != null ? { label: 'Recorded Documents', value: p.deed_count } : null,
      proField('Latest Grantee', p.latest_grantee),
      proField('Latest Sale Price', p.latest_sale_price ? formatCurrency(p.latest_sale_price) : null),
      proField('Latest Sale Date', p.latest_sale_date ? p.latest_sale_date.slice(0, 10) : null),
    ].filter(Boolean);
  } else if (isIL) {
    overviewItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'City', value: p.city || '-' },
      p.county ? { label: 'County', value: `${p.county} County` } : null,
      p.township ? { label: 'Township', value: p.township } : null,
      p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
      { label: 'State', value: 'Illinois' },
      p.pin ? { label: 'PIN', value: p.pin } : null,
      p.lot_size ? { label: 'Lot Size', value: `${formatNumber(p.lot_size)} ${p.lot_unit || ''}` } : null,
      p.current_use ? { label: 'Current Use', value: p.current_use } : null,
      p.transfer_count != null ? { label: 'PTAX Transfers', value: p.transfer_count } : null,
      p.cook_sales_count != null ? { label: 'Cook County Sales', value: p.cook_sales_count } : null,
      proField('Latest Buyer', p.latest_buyer),
      proField('Latest Sale Price', p.latest_sale_price ? formatCurrency(p.latest_sale_price) : null),
      proField('Latest Sale Date', p.latest_sale_date ? p.latest_sale_date.slice(0, 10) : null),
    ].filter(Boolean);
  } else if (isCT) {
    overviewItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'City', value: p.city || '-' },
      p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
      { label: 'State', value: 'Connecticut' },
      p.link ? { label: 'Parcel ID', value: p.link } : null,
      p.property_class_desc ? { label: 'Property Class', value: p.property_class_desc } : null,
      p.style ? { label: 'Style', value: p.style } : null,
      p.year_built ? { label: 'Year Built', value: p.year_built } : null,
      p.living_area_sqft ? { label: 'Living Area', value: formatSqft(p.living_area_sqft) } : null,
      p.gross_area_sqft ? { label: 'Gross Area', value: formatSqft(p.gross_area_sqft) } : null,
      p.bedrooms ? { label: 'Bedrooms', value: p.bedrooms } : null,
      p.baths ? { label: 'Bathrooms', value: `${p.baths}${p.half_baths ? `.${p.half_baths}` : ''}` } : null,
      p.total_rooms ? { label: 'Total Rooms', value: p.total_rooms } : null,
      p.stories ? { label: 'Stories', value: p.stories } : null,
      p.land_acres ? { label: 'Land Area', value: `${p.land_acres} ac` } : null,
      p.num_buildings ? { label: 'Buildings', value: p.num_buildings } : null,
      p.zone ? { label: 'Zone', value: p.zone } : null,
      proField('Owner', p.owner_name),
      proField('Co-Owner', p.co_owner),
      proField('Assessed Total', p.assessed_total ? formatCurrency(p.assessed_total) : null),
      proField('Appraised Total', p.appraised_total ? formatCurrency(p.appraised_total) : null),
      proField('Sale Grantor', p.sale_grantor),
      proField('Sale Grantee', p.sale_grantee),
      proField('Sale Price', p.sale_price ? formatCurrency(p.sale_price) : null),
      proField('Sale Date', p.sale_date ? p.sale_date.slice(0, 10) : null),
    ].filter(Boolean);
  } else if (isCA) {
    if (p.ain) {
      // LA County with backend-fetched assessor data
      overviewItems = [
        { label: 'Address', value: p.address || '-' },
        { label: 'City', value: p.city || '-' },
        p.county ? { label: 'County', value: p.county } : null,
        p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
        { label: 'AIN', value: p.ain },
        p.use_type ? { label: 'Use Type', value: p.use_type } : null,
        p.sqft_lot ? { label: 'Lot Size', value: formatSqft(p.sqft_lot) } : null,
        p.year_built ? { label: 'Year Built', value: p.year_built } : null,
        p.num_beds ? { label: 'Bedrooms', value: p.num_beds } : null,
        p.num_baths ? { label: 'Bathrooms', value: p.num_baths } : null,
        p.num_units ? { label: 'Units', value: p.num_units } : null,
        p.zoning ? { label: 'Zoning', value: p.zoning } : null,
        p.legal_description ? { label: 'Legal Description', value: p.legal_description } : null,
        p.tax_status ? { label: 'Tax Status', value: p.tax_status } : null,
        p.fire_hazard_zone != null ? { label: 'Fire Hazard Zone', value: formatFireHazardZone(p.fire_hazard_zone) } : null,
        proField('Owner', p.owner_name),
        proField('Assessed Land', p.assessed_land ? formatCurrency(p.assessed_land) : null),
        proField('Assessed Improvement', p.assessed_improvement ? formatCurrency(p.assessed_improvement) : null),
        p.assessed_year ? proField('Base Year', p.assessed_year) : null,
        (userTier !== 'free' && p.assessor_portal_url)
          ? { label: 'Assessor Portal', value: `<a href="${p.assessor_portal_url}" target="_blank" rel="noopener">View full records →</a>` }
          : (p.county ? { label: 'Assessor Portal', value: proLock } : null),
      ].filter(Boolean);
    } else {
      // Geocoder-only data (non-LA county, or LA backend fetch failed — client-side enrichment will try next)
      overviewItems = [
        { label: 'Address', value: p.address || '-' },
        { label: 'City', value: p.city || '-' },
        p.county ? { label: 'County', value: p.county } : null,
        p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
        { label: 'State', value: 'California' },
        p.county_fips ? { label: 'County FIPS', value: p.county_fips } : null,
        p.county_population ? { label: 'County Population', value: formatNumber(p.county_population) } : null,
        p.latitude ? { label: 'Latitude', value: p.latitude.toFixed(6) } : null,
        p.longitude ? { label: 'Longitude', value: p.longitude.toFixed(6) } : null,
        p.fire_hazard_zone != null ? { label: 'Fire Hazard Zone', value: formatFireHazardZone(p.fire_hazard_zone) } : null,
        (userTier !== 'free' && p.assessor_portal_url)
          ? { label: 'Assessor Portal', value: `<a href="${p.assessor_portal_url}" target="_blank" rel="noopener">View full records →</a>` }
          : (p.county ? { label: 'Assessor Portal', value: proLock } : null),
      ].filter(Boolean);
      // LA County: show loading placeholder if backend didn't get data (will be replaced async)
      if (p.county === 'Los Angeles') {
        overviewItems.splice(4, 0, { label: 'Property Data', value: '<span class="la-loading">Loading from LA County Assessor…</span>' });
      }
    }
  } else {
    overviewItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'Borough', value: p.boroughName || boroughName(p.borough) },
      { label: 'BBL', value: formatBBL(p.bbl) },
      proField('Owner', p.owner_name),
      { label: 'Lot Dimensions', value: formatDimensions(p.lot_front, p.lot_depth) },
      { label: 'Lot Area', value: formatSqft(p.lot_area) },
      { label: 'Building Dimensions', value: formatDimensions(p.bldg_front, p.bldg_depth) },
      { label: 'Building Area', value: formatSqft(p.bldg_area) },
      { label: 'Built FAR', value: d.built_far || p.far_built || '-' },
      { label: 'Max Residential FAR', value: d.official_res_far || '-' },
      proField('Max Buildable Sqft', maxBuildable ? formatSqft(maxBuildable) : null),
      proField('Unused Dev. Rights', unusedDev != null ? (unusedDev > 0 ? formatSqft(unusedDev) : 'Fully built') : null),
      { label: 'Max Height', value: d.max_height || '-' },
      { label: 'Year Built', value: p.year_built || '-' },
      { label: 'Building Class', value: p.bldg_class || '-' },
      { label: 'Land Use', value: landUseLabel(p.land_use) },
      { label: 'Floors', value: p.num_floors || '-' },
      { label: 'Total Units', value: formatNumber(p.units_total) },
      { label: 'Residential Units', value: formatNumber(p.units_res) },
    ].filter(Boolean);

    if (likelyStabilized) {
      overviewItems.push({ label: 'Rent Stabilization', value: 'Likely Stabilized (heuristic)' });
    }
  }

  renderGrid('overview-grid', overviewItems);
  renderCaliforniaDiligence(p, userTier);
  renderCaliforniaRecordTrail(p, userTier);
  renderCaliforniaOfferReadiness(p, userTier);
  renderCaliforniaEscrowChecklist(p, userTier);
  renderCaliforniaRiskTriage(p, userTier);
  renderMortgageCalc(p);

  // Building tab
  renderGrid('building-grid', [
    { label: 'Building Frontage', value: formatFeet(p.bldg_front) },
    { label: 'Building Depth', value: formatFeet(p.bldg_depth) },
    { label: 'Building Dimensions', value: formatDimensions(p.bldg_front, p.bldg_depth) },
    { label: 'Building Area', value: formatSqft(p.bldg_area) },
    { label: 'Residential Area', value: formatSqft(p.res_area) },
    { label: 'Commercial Area', value: formatSqft(p.com_area) },
    { label: 'Lot Frontage', value: formatFeet(p.lot_front) },
    { label: 'Lot Depth', value: formatFeet(p.lot_depth) },
    { label: 'Lot Dimensions', value: formatDimensions(p.lot_front, p.lot_depth) },
    { label: 'Lot Area', value: formatSqft(p.lot_area) },
    { label: 'Floors', value: p.num_floors || '-' },
    { label: 'Buildings on Lot', value: formatNumber(p.num_bldgs) },
    { label: 'Total Units', value: formatNumber(p.units_total) },
    { label: 'Residential Units', value: formatNumber(p.units_res) },
    { label: 'Year Built', value: p.year_built || '-' },
    { label: 'Building Class', value: p.bldg_class || '-' },
  ]);

  // Zoning tab
  const z = zoningData || {};
  const commFAR = d.official_comm_far || p.comm_far;
  const facilFAR = d.official_facil_far || p.facil_far;
  const commMaxBuildable = (commFAR && p.lot_area) ? Math.round(commFAR * p.lot_area) : null;
  const facilMaxBuildable = (facilFAR && p.lot_area) ? Math.round(facilFAR * p.lot_area) : null;

  const zoningItems = [
    { label: 'Primary Zoning', value: p.zoning_dist1 || '-' },
    { label: 'Secondary Zoning', value: p.zoning_dist2 || '-' },
    { label: 'Overlay', value: p.overlay1 || '-' },
    { label: 'Special District', value: p.spdist1 || '-' },
    { label: 'District Type', value: z.district_type ? z.district_type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase()) : '-' },
    { label: 'Residential FAR', value: d.official_res_far || p.far || '-' },
    { label: 'Commercial FAR', value: commFAR || '-' },
    { label: 'Facility FAR', value: facilFAR || '-' },
    { label: 'Built FAR', value: d.built_far || p.far_built || '-' },
    { label: 'Lot Area', value: formatSqft(p.lot_area) },
    { label: 'Max Buildable (Residential)', value: maxBuildable ? formatSqft(maxBuildable) : '-' },
    { label: 'Max Buildable (Commercial)', value: commMaxBuildable ? formatSqft(commMaxBuildable) : '-' },
    { label: 'Max Buildable (Facility)', value: facilMaxBuildable ? formatSqft(facilMaxBuildable) : '-' },
    { label: 'Current Building Area', value: formatSqft(p.bldg_area) },
    { label: 'Unused Dev. Rights', value: unusedDev != null ? (unusedDev > 0 ? formatSqft(unusedDev) : 'Fully built / over-built') : '-' },
    { label: 'Max Height', value: d.max_height || '-' },
    { label: 'Lot Coverage (Corner)', value: d.lot_coverage_corner ? `${d.lot_coverage_corner}%` : '-' },
    { label: 'Lot Coverage (Interior)', value: d.lot_coverage_interior ? `${d.lot_coverage_interior}%` : '-' },
    { label: 'Parking Requirement', value: d.parking_req || '-' },
  ];

  if (z.base_height_wide) zoningItems.push({ label: 'Base Height', value: z.base_height_wide });
  if (z.perimeter_wall_height) zoningItems.push({ label: 'Perimeter Wall Height', value: z.perimeter_wall_height });
  if (z.front_yard_min) zoningItems.push({ label: 'Front Yard (min)', value: `${z.front_yard_min} ft` });
  if (z.rear_yard_min) zoningItems.push({ label: 'Rear Yard (min)', value: `${z.rear_yard_min} ft` });

  zoningItems.push(
    { label: 'Community District', value: p.cd || '-' },
    { label: 'Land Use', value: landUseLabel(p.land_use) },
  );

  if (z.notes) zoningItems.push({ label: 'Notes', value: z.notes });

  renderGrid('zoning-grid', zoningItems);

  // Tax tab
  if (isPHL) {
    const taxItems = [
      { label: 'City', value: 'Philadelphia' },
      { label: 'County', value: 'Philadelphia' },
      p.opa_account_num ? { label: 'OPA Account #', value: p.opa_account_num } : null,
      p.deed_count != null ? { label: 'Recorded Documents', value: p.deed_count } : null,
    ].filter(Boolean);
    if (p.opa_account_num) {
      taxItems.push({
        label: 'OPA Property Search',
        value: `<a href="https://property.phila.gov/?p=${encodeURIComponent(p.opa_account_num)}" target="_blank" rel="noopener">View on property.phila.gov →</a>`,
      });
    }
    if (userTier !== 'free' && Array.isArray(p.deeds) && p.deeds.length > 0) {
      taxItems.push({
        label: 'Recent Deeds',
        value: p.deeds.slice(0, 5).map(d => {
          const price = d.total_consideration ? formatCurrency(d.total_consideration) : '—';
          const date = d.recording_date ? d.recording_date.slice(0, 10) : '';
          const type = d.document_type || '';
          return `${date} · ${type} · ${price}`;
        }).join('<br>'),
      });
    }
    renderGrid('tax-grid', taxItems);
  } else if (isIL) {
    const taxItems = [
      p.county ? { label: 'County', value: `${p.county} County` } : null,
      p.township ? { label: 'Township', value: p.township } : null,
      p.pin ? { label: 'PIN', value: p.pin } : null,
      p.transfer_count != null ? { label: 'PTAX Transfers', value: p.transfer_count } : null,
      p.cook_sales_count != null ? { label: 'Cook County Sales', value: p.cook_sales_count } : null,
    ].filter(Boolean);
    if (userTier !== 'free' && Array.isArray(p.transfers) && p.transfers.length > 0) {
      taxItems.push({
        label: 'Recent Transfers',
        value: p.transfers.slice(0, 5).map(t => {
          const price = t.consideration ? formatCurrency(t.consideration) : '—';
          const date = t.date_recorded ? t.date_recorded.slice(0, 10) : '';
          return `${date} · ${t.instrument_type || 'Transfer'} · ${price}`;
        }).join('<br>'),
      });
    }
    if (userTier !== 'free' && Array.isArray(p.cook_sales) && p.cook_sales.length > 0) {
      taxItems.push({
        label: 'Cook County Sales',
        value: p.cook_sales.slice(0, 5).map(s => {
          const price = s.sale_price ? formatCurrency(s.sale_price) : '—';
          const date = s.sale_date ? s.sale_date.slice(0, 10) : '';
          return `${date} · ${s.deed_type || 'Sale'} · ${price}`;
        }).join('<br>'),
      });
    }
    renderGrid('tax-grid', taxItems);
  } else if (isCT) {
    const taxItems = [
      proField('Assessed Total', p.assessed_total ? formatCurrency(p.assessed_total) : null),
      proField('Assessed Building', p.assessed_building ? formatCurrency(p.assessed_building) : null),
      proField('Assessed Land', p.assessed_land ? formatCurrency(p.assessed_land) : null),
      proField('Appraised Total', p.appraised_total ? formatCurrency(p.appraised_total) : null),
      proField('Appraised Building', p.appraised_building ? formatCurrency(p.appraised_building) : null),
      proField('Appraised Land', p.appraised_land ? formatCurrency(p.appraised_land) : null),
      p.valuation_year ? { label: 'Valuation Year', value: p.valuation_year } : null,
      proField('Book/Page', p.book_page),
      proField('Sale Price', p.sale_price ? formatCurrency(p.sale_price) : null),
      proField('Sale Date', p.sale_date ? p.sale_date.slice(0, 10) : null),
      proField('Prior Sale Price', p.prior_sale_price ? formatCurrency(p.prior_sale_price) : null),
      proField('Prior Sale Date', p.prior_sale_date ? p.prior_sale_date.slice(0, 10) : null),
    ].filter(Boolean);
    renderGrid('tax-grid', taxItems);
  } else if (isCA) {
    const taxItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'City', value: p.city || '-' },
      p.county ? { label: 'County', value: p.county } : null,
      p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
      p.county_population ? { label: 'County Population', value: formatNumber(p.county_population) } : null,
    ].filter(Boolean);
    if (p.assessor_portal_url) {
      taxItems.push({
        label: 'County Assessor Portal',
        value: `<a href="${p.assessor_portal_url}" target="_blank" rel="noopener">View full records →</a>`,
      });
    } else if (userTier === 'free' && p.county) {
      taxItems.push({ label: 'County Assessor Portal', value: proLock });
    }
    renderGrid('tax-grid', taxItems);
  } else if (isNYS) {
    renderGrid('tax-grid', [
      proField('Market Value', p.full_market_value ? formatCurrency(p.full_market_value) : null),
      proField('Total Assessment', p.assessed_total ? formatCurrency(p.assessed_total) : null),
      proField('Land Assessment', p.assessed_land ? formatCurrency(p.assessed_land) : null),
      proField('County Taxable', p.county_taxable ? formatCurrency(p.county_taxable) : null),
      proField('Town Taxable', p.town_taxable ? formatCurrency(p.town_taxable) : null),
      proField('School Taxable', p.school_taxable ? formatCurrency(p.school_taxable) : null),
      p.school_district ? { label: 'School District', value: p.school_district } : null,
    ].filter(Boolean));
  } else {
    renderGrid('tax-grid', [
      { label: 'Total Assessed Value', value: formatCurrency(p.assessed_total) },
      { label: 'Land Assessed Value', value: formatCurrency(p.assessed_land) },
      { label: 'Building Assessed Value', value: formatCurrency((p.assessed_total || 0) - (p.assessed_land || 0)) },
    ]);
  }

  // Pro tabs — show lock or load data
  setupProTabs();

  // Load record counts for tab badges (prefetch all Socrata data) — NYC only
  if (!isOutOfState) {
    loadRecordCounts();
  }

  // Tab switching
  document.querySelectorAll('.tab').forEach(tab => {
    tab.addEventListener('click', () => switchTab(tab.dataset.tab));
  });

  // Pro users: preload all data in background (NYC only — out-of-state regions have no Socrata NYC data)
  if (userTier !== 'free' && !isOutOfState) {
    loadHealthScore();
    loadDetailData();
    loadComparableSales();
    loadECB();
    loadHPDRegistrations();
    loadDOBJobs();
    loadCofOcc();
    load311Complaints();
    loadHealthData();
    if (userTier !== 'free') {
      loadTaxHistory();
      loadTaxBalance();
    }
  }

  // LA County: if backend fetch didn't get assessor data (e.g., Workers blocked by Imperva),
  // retry via browser-side fetch which may bypass bot protection
  if (isCA && p.county === 'Los Angeles' && !p.ain) {
    enrichLACountyProperty(p, userTier);
  }
}

// Returns HTML badge for CalFire Fire Hazard Severity Zone
function formatFireHazardZone(zone) {
  if (!zone) return zone;
  const colors = {
    'Very High': { bg: '#fef2f2', color: '#b91c1c', border: '#fca5a5' },
    'High':      { bg: '#fff7ed', color: '#c2410c', border: '#fdba74' },
    'Moderate':  { bg: '#fefce8', color: '#92400e', border: '#fde68a' },
  };
  const s = colors[zone];
  if (!s) return zone;
  return `<span style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:.8em;font-weight:600;background:${s.bg};color:${s.color};border:1px solid ${s.border}">&#x1F525; ${zone}</span>`;
}

function renderCaliforniaDiligence(property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid) return;

  const existing = document.getElementById('ca-diligence-card');
  if (property?.region !== 'ca') {
    if (existing) existing.remove();
    return;
  }

  const snapshot = getCaliforniaDiligenceSnapshot(property, tier);
  const badgeTone = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    low: 'background:#f0fdf4;color:#15803d;border-color:#86efac;',
    good: 'background:#ecfeff;color:#0f766e;border-color:#67e8f9;',
    neutral: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const badgeHtml = snapshot.badges.map((badge) =>
    `<span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;border:1px solid;${badgeTone[badge.tone] || badgeTone.neutral}font-size:12px;font-weight:700;">${badge.label}</span>`
  ).join('');
  const bulletHtml = snapshot.bullets.map((bullet) => `<li>${bullet}</li>`).join('');
  const html = `
    <div id="ca-diligence-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#fff 0%,#f8fafc 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap;margin-bottom:10px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">California</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${snapshot.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:720px;">${snapshot.summary}</div>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">${badgeHtml}</div>
      </div>
      <ul style="margin:0;padding-left:18px;color:var(--text);display:grid;gap:8px;">
        ${bulletHtml}
      </ul>
    </div>
  `;

  if (existing) {
    existing.outerHTML = html;
    return;
  }

  overviewGrid.insertAdjacentHTML('afterend', html);
}

function renderCaliforniaRecordTrail(property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid) return;

  const existing = document.getElementById('ca-record-trail-card');
  if (property?.region !== 'ca') {
    if (existing) existing.remove();
    return;
  }

  const trail = getCaliforniaRecordTrail(property, tier);
  const badgeTone = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    low: 'background:#f0fdf4;color:#15803d;border-color:#86efac;',
    good: 'background:#ecfeff;color:#0f766e;border-color:#67e8f9;',
    neutral: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const badgeHtml = trail.badges.map((badge) =>
    `<span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;border:1px solid;${badgeTone[badge.tone] || badgeTone.neutral}font-size:12px;font-weight:700;">${badge.label}</span>`
  ).join('');
  const fieldHtml = trail.fields.map((field) => `
    <div style="padding:14px;border:1px solid rgba(148,163,184,.22);border-radius:14px;background:#fff;">
      <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:6px;">${field.label}</div>
      <div style="font-size:14px;line-height:1.55;color:var(--text);">${field.value}</div>
    </div>
  `).join('');
  const html = `
    <div id="ca-record-trail-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#f8fafc 0%,#eef6ff 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">California</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${trail.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:760px;">${trail.summary}</div>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">${badgeHtml}</div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:12px;">
        ${fieldHtml}
      </div>
    </div>
  `;

  const diligenceCard = document.getElementById('ca-diligence-card');
  if (existing) {
    existing.outerHTML = html;
    return;
  }
  (diligenceCard || overviewGrid).insertAdjacentHTML('afterend', html);
}

function renderCaliforniaEscrowChecklist(property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid) return;

  const existing = document.getElementById('ca-escrow-checklist-card');
  if (property?.region !== 'ca') {
    if (existing) existing.remove();
    return;
  }

  const checklist = getCaliforniaEscrowChecklist(property, tier);
  const badgeTone = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    low: 'background:#f0fdf4;color:#15803d;border-color:#86efac;',
    good: 'background:#ecfeff;color:#0f766e;border-color:#67e8f9;',
    neutral: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const statusTone = {
    ready: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
    verify: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    locked: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const badgeHtml = checklist.badges.map((badge) =>
    `<span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;border:1px solid;${badgeTone[badge.tone] || badgeTone.neutral}font-size:12px;font-weight:700;">${badge.label}</span>`
  ).join('');
  const rowHtml = checklist.checklist.map((item) => `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;align-items:start;padding:14px;border:1px solid rgba(148,163,184,.22);border-radius:12px;background:#fff;">
      <div style="font-size:14px;font-weight:800;color:var(--text);">${item.label}</div>
      <div><span style="display:inline-flex;padding:3px 8px;border-radius:999px;border:1px solid;${statusTone[item.status] || statusTone.verify}font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;">${item.status}</span></div>
      <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${item.detail}</div>
    </div>
  `).join('');
  const nextPriorityHtml = checklist.next_priority ? `
    <div style="margin:0 0 12px;padding:12px 14px;border:1px solid rgba(14,165,233,.24);border-radius:12px;background:#f0f9ff;">
      <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#0369a1;margin-bottom:4px;">Next Priority</div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:4px;">
        <strong style="font-size:15px;color:var(--text);">${checklist.next_priority.label}</strong>
        <span style="display:inline-flex;padding:3px 8px;border-radius:999px;border:1px solid;${statusTone[checklist.next_priority.status] || statusTone.verify}font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;">${checklist.next_priority.status}</span>
      </div>
      <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${checklist.next_priority.detail}</div>
    </div>
  ` : '';
  const html = `
    <div id="ca-escrow-checklist-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#fff 0%,#f6fbff 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">California</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${checklist.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:760px;">${checklist.summary}</div>
        </div>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">${badgeHtml}</div>
      </div>
      ${nextPriorityHtml}
      <div style="display:grid;gap:10px;">${rowHtml}</div>
    </div>
  `;

  const readinessCard = document.getElementById('ca-offer-readiness-card');
  const recordTrailCard = document.getElementById('ca-record-trail-card');
  const diligenceCard = document.getElementById('ca-diligence-card');
  if (existing) {
    existing.outerHTML = html;
    return;
  }
  (readinessCard || recordTrailCard || diligenceCard || overviewGrid).insertAdjacentHTML('afterend', html);
}

function renderCaliforniaRiskTriage(property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid) return;

  const existing = document.getElementById('ca-risk-triage-card');
  if (property?.region !== 'ca') {
    if (existing) existing.remove();
    return;
  }

  const triage = getCaliforniaRiskTriage(property, tier);
  const toneStyle = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    good: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
    neutral: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const levelTone = {
    clear: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
    verify: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    escalate: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    locked: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const badgeHtml = triage.badges.map((badge) =>
    `<span style="display:inline-flex;align-items:center;padding:4px 10px;border-radius:999px;border:1px solid;${toneStyle[badge.tone] || toneStyle.neutral}font-size:12px;font-weight:700;">${badge.label}</span>`
  ).join('');
  const riskRows = triage.risks.map((risk) => `
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:12px;align-items:start;padding:14px;border:1px solid rgba(148,163,184,.22);border-radius:12px;background:#fff;">
      <div style="font-size:14px;font-weight:800;color:var(--text);">${risk.label}</div>
      <div><span style="display:inline-flex;padding:3px 8px;border-radius:999px;border:1px solid;${levelTone[risk.level] || levelTone.verify}font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;">${risk.level}</span></div>
      <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${risk.detail}</div>
    </div>
  `).join('');
  const clearancePlanHtml = (triage.clearance_plan || []).length ? `
    <div style="margin:0 0 12px;padding:12px 14px;border:1px solid rgba(22,163,74,.22);border-radius:12px;background:#f7fee7;">
      <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#3f6212;margin-bottom:8px;">Clearance order</div>
      <div style="display:grid;gap:8px;">
        ${triage.clearance_plan.map((item) => `
          <div style="display:grid;grid-template-columns:auto minmax(0,1fr);gap:10px;align-items:start;font-size:13px;color:var(--text-muted);">
            <strong style="display:inline-flex;width:24px;height:24px;border-radius:999px;align-items:center;justify-content:center;background:#ecfccb;color:#365314;">${item.step}</strong>
            <span><strong style="display:block;color:var(--text);margin-bottom:2px;">${item.label}</strong>${item.detail}</span>
          </div>
        `).join('')}
      </div>
    </div>
  ` : '';
  const nextPriorityHtml = triage.next_priority ? `
    <div style="margin:0 0 12px;padding:12px 14px;border:1px solid rgba(14,165,233,.24);border-radius:12px;background:#f0f9ff;">
      <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#0369a1;margin-bottom:4px;">Risk to clear next</div>
      <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:4px;">
        <strong style="font-size:15px;color:var(--text);">${triage.next_priority.label}</strong>
        <span style="display:inline-flex;padding:3px 8px;border-radius:999px;border:1px solid;${levelTone[triage.next_priority.level] || levelTone.verify}font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;">${triage.next_priority.level}</span>
      </div>
      <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${triage.next_priority.detail}</div>
    </div>
  ` : '';
  const html = `
    <div id="ca-risk-triage-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#fff 0%,#f8fbf6 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">California</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${triage.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:760px;">${triage.summary}</div>
        </div>
        <div style="min-width:180px;padding:12px 14px;border:1px solid;${toneStyle[triage.tone] || toneStyle.neutral}border-radius:14px;text-align:center;">
          <div style="font-size:13px;font-weight:900;line-height:1.35;">${triage.status}</div>
          <div style="font-size:11px;color:inherit;opacity:.8;margin-top:6px;">${triage.counts.clear} clear · ${triage.counts.verify} verify · ${triage.counts.escalate} escalate · ${triage.counts.locked} Pro-gated</div>
        </div>
      </div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px;">${badgeHtml}</div>
      ${nextPriorityHtml}
      ${clearancePlanHtml}
      <div style="display:grid;gap:10px;">${riskRows}</div>
    </div>
  `;

  const escrowCard = document.getElementById('ca-escrow-checklist-card');
  const readinessCard = document.getElementById('ca-offer-readiness-card');
  const recordTrailCard = document.getElementById('ca-record-trail-card');
  const diligenceCard = document.getElementById('ca-diligence-card');
  if (existing) {
    existing.outerHTML = html;
    return;
  }
  (escrowCard || readinessCard || recordTrailCard || diligenceCard || overviewGrid).insertAdjacentHTML('afterend', html);
}

function renderCaliforniaOfferReadiness(property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid) return;

  const existing = document.getElementById('ca-offer-readiness-card');
  if (property?.region !== 'ca') {
    if (existing) existing.remove();
    return;
  }

  const readiness = getCaliforniaOfferReadiness(property, tier);
  const toneStyle = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    good: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
  };
  const checkTone = {
    ready: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
    verify: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    locked: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const checkHtml = readiness.checks.map((check) => `
    <div style="padding:12px;border:1px solid rgba(148,163,184,.22);border-radius:12px;background:#fff;">
      <div style="display:flex;justify-content:space-between;gap:8px;align-items:center;margin-bottom:6px;">
        <strong style="font-size:14px;color:var(--text);">${check.label}</strong>
        <span style="display:inline-flex;padding:3px 8px;border-radius:999px;border:1px solid;${checkTone[check.status] || checkTone.verify}font-size:11px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;">${check.status}</span>
      </div>
      <div style="font-size:13px;line-height:1.45;color:var(--text-muted);">${check.detail}</div>
    </div>
  `).join('');
  const html = `
    <div id="ca-offer-readiness-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#f8fffb 0%,#f3f8ff 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:14px;align-items:flex-start;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">California</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${readiness.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:760px;">${readiness.summary}</div>
        </div>
        <div style="min-width:160px;padding:12px 14px;border:1px solid rgba(148,163,184,.24);border-radius:14px;background:#fff;text-align:center;">
          <div style="font-size:28px;font-weight:900;color:var(--text);line-height:1;">${readiness.score}</div>
          <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-top:4px;">Readiness score</div>
          <div style="display:inline-flex;margin-top:8px;padding:4px 9px;border-radius:999px;border:1px solid;${toneStyle[readiness.tone] || toneStyle.medium}font-size:12px;font-weight:800;">${readiness.status}</div>
          <div style="font-size:11px;color:var(--text-muted);margin-top:7px;">${readiness.counts.ready} ready · ${readiness.counts.verify} verify · ${readiness.counts.locked} Pro-gated</div>
        </div>
      </div>
      <div style="margin:0 0 12px;padding:12px 14px;border:1px solid rgba(14,165,233,.24);border-radius:12px;background:#f0f9ff;">
        <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#0369a1;margin-bottom:4px;">Next offer check</div>
        <div style="font-size:15px;font-weight:800;color:var(--text);margin-bottom:3px;">${readiness.next_step.label}</div>
        <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${readiness.next_step.detail}</div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:10px;">${checkHtml}</div>
    </div>
  `;

  const recordTrailCard = document.getElementById('ca-record-trail-card');
  const diligenceCard = document.getElementById('ca-diligence-card');
  if (existing) {
    existing.outerHTML = html;
    return;
  }
  (recordTrailCard || diligenceCard || overviewGrid).insertAdjacentHTML('afterend', html);
}

function renderGrid(containerId, items) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.innerHTML = items.map(item => `
    <div class="data-item">
      <div class="label">${item.label}</div>
      <div class="value${typeof item.value === 'string' && item.value.length > 30 ? ' small' : ''}">${item.value}</div>
    </div>
  `).join('');
}

// LA County: fetch property data from Assessor API (browser-side, bypasses Imperva)
async function enrichLACountyProperty(p, tier) {
  const LA_SEARCH = 'https://portal.assessor.lacounty.gov/api/search';
  const LA_DETAIL = 'https://portal.assessor.lacounty.gov/api/parceldetail';
  const proLock = '<a href="/pricing" class="pro-field-lock">PRO</a>';
  function proField(label, value) {
    if (value == null || value === '-' || value === '' || value === 0) return null;
    if (tier === 'free') return { label, value: proLock };
    return { label, value };
  }

  try {
    // Step 1: Search by address to get AIN
    const searchParams = new URLSearchParams({ search: p.address });
    if (p.zipcode) searchParams.set('zip', p.zipcode);
    const searchResp = await fetch(`${LA_SEARCH}?${searchParams}`);
    if (!searchResp.ok) throw new Error('search failed');
    const searchData = await searchResp.json();
    if (!searchData.Parcels || searchData.Parcels.length === 0) throw new Error('no parcels');

    // Narrow candidates by matching street address
    const addrUpper = (p.address || '').toUpperCase().trim();
    const candidates = searchData.Parcels.filter(pr =>
      pr.SitusStreet && pr.SitusStreet.toUpperCase().startsWith(addrUpper.split(',')[0])
    );
    const parcels = candidates.length > 0 ? candidates : searchData.Parcels;

    // Step 2: Try candidates to find active (non-DELETED) parcel
    let d = null;
    for (let i = 0; i < Math.min(parcels.length, 3); i++) {
      const ain = parcels[i].AIN;
      if (!ain) continue;
      const detailResp = await fetch(`${LA_DETAIL}?ain=${ain}`);
      if (!detailResp.ok) continue;
      const detailData = await detailResp.json();
      const parcel = detailData.Parcel || detailData;
      if (parcel.ParcelStatus === 'DELETED') continue;
      d = parcel;
      break;
    }
    if (!d) throw new Error('all deleted');

    // Re-render Overview with real property data
    const overviewItems = [
      { label: 'Address', value: p.address || '-' },
      { label: 'City', value: p.city || '-' },
      p.county ? { label: 'County', value: p.county } : null,
      p.zipcode ? { label: 'ZIP Code', value: p.zipcode } : null,
      { label: 'AIN', value: d.AIN },
      d.UseType ? { label: 'Use Type', value: d.UseType } : null,
      d.SqftLot ? { label: 'Lot Size', value: formatSqft(d.SqftLot) } : null,
      d.YearBuilt ? { label: 'Year Built', value: d.YearBuilt } : null,
      d.NumOfBeds ? { label: 'Bedrooms', value: d.NumOfBeds } : null,
      d.NumOfBaths ? { label: 'Bathrooms', value: d.NumOfBaths } : null,
      d.NumOfUnits ? { label: 'Units', value: d.NumOfUnits } : null,
      (d.ZoningPDB || '').trim() ? { label: 'Zoning', value: (d.ZoningPDB || '').trim() } : null,
      d.LegalDescription ? { label: 'Legal Description', value: d.LegalDescription } : null,
      d.TaxStatus ? { label: 'Tax Status', value: d.TaxStatus } : null,
      proField('Owner', d.OwnerName || null),
      proField('Assessed Land', d.BaseValue_Land ? formatCurrency(d.BaseValue_Land) : null),
      proField('Assessed Improvement', d.BaseValue_Imp ? formatCurrency(d.BaseValue_Imp) : null),
      d.BaseValue_Year ? proField('Base Year', d.BaseValue_Year) : null,
      p.assessor_portal_url
        ? { label: 'Assessor Portal', value: `<a href="${p.assessor_portal_url}" target="_blank" rel="noopener">View full records →</a>` }
        : (tier !== 'free' ? null : { label: 'Assessor Portal', value: proLock }),
    ].filter(Boolean);
    propertyData.ain = d.AIN;
    propertyData.use_type = d.UseType;
    propertyData.year_built = d.YearBuilt;
    propertyData.sqft_lot = d.SqftLot;
    propertyData.tax_status = d.TaxStatus;
    propertyData.assessed_land = d.BaseValue_Land;
    propertyData.assessed_improvement = d.BaseValue_Imp;

    renderGrid('overview-grid', overviewItems);
    renderCaliforniaDiligence(propertyData, tier);
    renderCaliforniaRecordTrail(propertyData, tier);
    renderCaliforniaOfferReadiness(propertyData, tier);
    renderCaliforniaEscrowChecklist(propertyData, tier);
    renderCaliforniaRiskTriage(propertyData, tier);

    // Re-render Tax tab with assessed values
    const taxItems = [
      { label: 'AIN', value: d.AIN },
      d.TaxStatus ? { label: 'Tax Status', value: d.TaxStatus } : null,
      d.TaxRateArea ? { label: 'Tax Rate Area', value: d.TaxRateArea } : null,
      proField('Assessed Land', d.BaseValue_Land ? formatCurrency(d.BaseValue_Land) : null),
      proField('Assessed Improvement', d.BaseValue_Imp ? formatCurrency(d.BaseValue_Imp) : null),
      (d.BaseValue_Land || d.BaseValue_Imp) ? proField('Total Assessed', formatCurrency((d.BaseValue_Land || 0) + (d.BaseValue_Imp || 0))) : null,
      d.BaseValue_Year ? proField('Base Year', d.BaseValue_Year) : null,
    ].filter(Boolean);
    if (p.assessor_portal_url) {
      taxItems.push({ label: 'Assessor Portal', value: `<a href="${p.assessor_portal_url}" target="_blank" rel="noopener">View full records →</a>` });
    }
    renderGrid('tax-grid', taxItems);

    // Update tags with property type
    const tags = document.getElementById('prop-tags');
    if (tags) {
      const tagItems = [];
      if (d.UseType) tagItems.push(d.UseType);
      if (p.county) tagItems.push(`${p.county} County`);
      if (d.YearBuilt) tagItems.push(`Built ${d.YearBuilt}`);
      tagItems.push('California');
      tags.innerHTML = tagItems.map(t => `<span class="tag">${t}</span>`).join('');
    }

    // Enriched data has already been stored on the property object for other features.
  } catch (e) {
    // Remove loading placeholder — keep the geocoder data as-is
    const loadingEl = document.querySelector('.la-loading');
    if (loadingEl) loadingEl.closest('.data-item')?.remove();
  }
}

function switchTab(tabId) {
  if (typeof window.trackEvent === 'function') window.trackEvent('tab', 'click', tabId);
  document.querySelectorAll('.tab').forEach(t => t.classList.toggle('active', t.dataset.tab === tabId));
  document.querySelectorAll('.tab-content').forEach(c => c.classList.toggle('active', c.id === `tab-${tabId}`));

  // Load building profile lazily
  if (tabId === 'building' && !buildingProfileLoaded) {
    loadBuildingProfile();
  }

  // Load new Pro features lazily
  if (tabId === 'investment' && !investmentAnalysisLoaded) {
    loadInvestmentAnalysis();
  }
  if (tabId === 'market' && !marketTrendsLoaded) {
    loadMarketTrends();
  }
  if (tabId === 'comparables' && !comparablesLoaded) {
    loadComparables();
  }

  // Initialize map lazily
  if (tabId === 'map' && propertyData?.latitude && propertyData?.longitude) {
    setTimeout(() => {
      const m = initMap('property-map');
      if (m) {
        m.on('load', () => {
          flyTo(propertyData.longitude, propertyData.latitude);
          setMarker(propertyData.longitude, propertyData.latitude,
            `<strong>${propertyData.address || ''}</strong>`);
          // Add building footprint polygon if available
          if (buildingProfileData?.footprint) {
            addFootprintToMap(m, buildingProfileData.footprint);
          }
        });
      }
    }, 100);
  }

  // Load pro data lazily
  if (userTier === 'free') {
    if (['ownership', 'sales', 'permits', 'violations', 'mortgages', 'complaints', 'health', 'investment', 'market', 'comparables'].includes(tabId)
      && typeof window.trackEvent === 'function') {
      window.trackEvent('pro', 'prompt_view', tabId, String(propertyData?.bbl || ''));
    }
    return;
  }

  if (['ownership', 'sales', 'permits', 'violations', 'mortgages'].includes(tabId) && !detailData) {
    loadDetailData();
  }
  if (tabId === 'sales' && !salesLoaded) {
    loadComparableSales();
  }
  if (tabId === 'violations' && !ecbLoaded) {
    loadECB();
  }
  if (tabId === 'ownership' && !hpdRegLoaded) {
    loadHPDRegistrations();
  }
  if (tabId === 'ownership' && !llcPortfolioLoaded) {
    loadLLCPortfolio();
  }
  if (tabId === 'tax' && !taxHistoryLoaded) {
    loadTaxHistory();
  }
  if (tabId === 'tax' && !taxBalanceLoaded) {
    loadTaxBalance();
  }
  if (tabId === 'permits' && !dobJobsLoaded) {
    loadDOBJobs();
  }
  if (tabId === 'permits' && !cofOccLoaded) {
    loadCofOcc();
  }
  if (tabId === 'complaints' && !complaintsLoaded) {
    load311Complaints();
  }
  if (tabId === 'health' && !healthDataLoaded) {
    loadHealthData();
  }
}

const PRO_TAB_INFO = {
  'ownership-content': {
    title: 'Ownership History',
    desc: 'Deed history, transfers, and ownership records from NYC ACRIS.',
    countId: 'pro-count-ownership',
  },
  'mortgages-content': {
    title: 'Mortgages & Liens',
    desc: 'Mortgage filings, satisfactions, assignments, and UCC/lien filings.',
    countId: 'pro-count-mortgages',
  },
  'sales-content': {
    title: 'Sales History',
    desc: 'Sale history with transaction prices and comparable sales analysis.',
    countId: 'pro-count-sales',
  },
  'permits-content': {
    title: 'Permits & Filings',
    desc: 'DOB permits, job filings, and certificates of occupancy.',
    countId: 'pro-count-permits',
  },
  'violations-content': {
    title: 'Violations',
    desc: 'DOB, HPD, and ECB violations with fine amounts and status.',
    countId: 'pro-count-violations',
  },
  'complaints-content': {
    title: '311 Complaints',
    desc: 'Reported complaints including noise, heat, and maintenance issues.',
    countId: 'pro-count-complaints',
  },
  'health-content': {
    title: 'Health & Inspections',
    desc: 'Restaurant grades, rodent inspections, and bedbug reports from NYC DOHMH.',
    countId: 'pro-count-health',
  },
};

function getProLockBullets(tabId) {
  const p = propertyData || {};
  const bullets = {
    'ownership-content': [
      p.owner_name ? `Current owner record: ${p.owner_name}` : 'See the current owner and transfer chain',
      p.latest_sale_date ? `Trace transfers back from ${p.latest_sale_date.slice(0, 10)}` : 'Trace the full deed history for this parcel',
      'Spot LLC flips, trusts, and off-market ownership changes',
    ],
    'mortgages-content': [
      'See open mortgages, satisfactions, assignments, and lien filings',
      p.assessed_total ? `Compare debt stack against ${formatCurrency(p.assessed_total)} in assessed value` : 'Check whether the property is over-levered before you bid',
      'Surface financing events before they show up in listing copy',
    ],
    'sales-content': [
      p.sale_price ? `Latest recorded sale: ${formatCurrency(p.sale_price)}` : 'Unlock full sale-price history for this address',
      'Review comparables and price-per-square-foot trends in one view',
      'Use recent transfers to pressure-test your offer before calling a broker',
    ],
    'permits-content': [
      'See DOB jobs, permits, and certificate-of-occupancy changes',
      p.year_built ? `Validate improvements against a ${p.year_built} vintage building` : 'Validate improvements against official permit history',
      'Catch gut-renovation and alteration work hidden from listing pages',
    ],
    'violations-content': [
      'Surface DOB, HPD, and ECB issues with status and fine exposure',
      'Find expensive problems before inspection day',
      'Separate cosmetic rehabs from headache buildings fast',
    ],
    'complaints-content': [
      'Read complaint volume by category, not just a single headline issue',
      'See if noise, heat, pests, or maintenance problems keep recurring',
      'Use complaint history as a tenant-risk and building-management signal',
    ],
    'health-content': [
      'Check restaurant grades, rodent activity, and bedbug inspection signals nearby',
      'Spot recurring sanitation issues that can impact value and rentability',
      'Get neighborhood health context beyond the listing photos',
    ],
  };

  return bullets[tabId] || [
    'Unlock the full record set for this property',
    'See the details behind the locked tab count',
    'Use the data before you make an offer or schedule a showing',
  ];
}

function getProLockRoute(tabId, path = '/pricing.html') {
  const params = new URLSearchParams({
    from: 'property-pro-lock',
    tab: tabId.replace('-content', ''),
  });
  if (propertyData?.bbl) params.set('bbl', String(propertyData.bbl));
  return `${path}?${params.toString()}`;
}

function setupProTabs() {
  if (userTier !== 'free') return;

  for (const [id, info] of Object.entries(PRO_TAB_INFO)) {
    const el = document.getElementById(id);
    if (!el) continue;
    const tabName = id.replace('-content', '');
    const unlockHref = getProLockRoute(id);
    const planHref = `${getProLockRoute(id)}#comparison`;
    el.innerHTML = `
      <div class="pro-lock">
        <h3>${info.title}</h3>
        <p>${info.desc}</p>
        ${info.countId ? `<p class="pro-lock-records" id="${info.countId}"></p>` : ''}
        <ul class="pro-lock-bullets">
          ${getProLockBullets(id).map((bullet) => `<li>${bullet}</li>`).join('')}
        </ul>
        <div class="pro-lock-cta-row">
          <a href="${unlockHref}" class="btn btn-primary" data-pro-cta="${tabName}" data-pro-action="unlock">Unlock This Property</a>
          <a href="${planHref}" class="btn btn-secondary" data-pro-cta="${tabName}" data-pro-action="plans">See Plans</a>
        </div>
        <p class="pro-lock-note">Pro starts at $4.99/mo and unlocks 100 lookups per day.</p>
      </div>`;
  }

  document.querySelectorAll('[data-pro-cta]').forEach((link) => {
    link.addEventListener('click', () => {
      if (typeof window.trackEvent === 'function') {
        window.trackEvent('pro', 'prompt_click', `${link.dataset.proCta}:${link.dataset.proAction}`, String(propertyData?.bbl || ''));
      }
    });
  });
}

// ─── Record Counts (tab badges + pro-lock counts) ───

async function loadRecordCounts() {
  if (!propertyData?.bbl) return;

  try {
    // Prefetch triggers Socrata data fetch + D1 save for all datasets,
    // returns accurate per-tab counts. Free tier — runs for all users.
    const counts = await apiGet(`/api/prefetch?bbl=${propertyData.bbl}`);
    updateTabBadges(counts);
    renderRecordActionPlan(counts, propertyData, userTier);
    if (userTier === 'free') {
      updateProLockCounts(counts);
    }
  } catch (e) {
    // Non-critical — silently fail
  }
}

function updateTabBadges(counts) {
  const badges = {
    ownership: counts.ownership,
    mortgages: counts.mortgages,
    sales: counts.sales,
    permits: counts.permits,
    violations: counts.violations,
    complaints: counts.complaints,
    health: counts.health,
  };

  for (const [tab, count] of Object.entries(badges)) {
    setTabBadge(tab, count);
  }
}

function setTabBadge(tabName, count) {
  const btn = document.querySelector(`.tab[data-tab="${tabName}"]`);
  if (!btn) return;
  // Remove existing badge
  const existing = btn.querySelector('.tab-count');
  if (existing) existing.remove();
  if (!count) return;
  const badge = document.createElement('span');
  badge.className = 'tab-count';
  badge.textContent = count > 999 ? '999+' : count;
  btn.appendChild(badge);
}

function renderRecordActionPlan(counts, property, tier) {
  const overviewGrid = document.getElementById('overview-grid');
  if (!overviewGrid || !property?.bbl) return;

  const existing = document.getElementById('record-action-plan-card');
  const plan = getRecordCountActionPlan(counts, property, tier);
  const toneStyle = {
    high: 'background:#fff1f2;color:#be123c;border-color:#fda4af;',
    medium: 'background:#fff7ed;color:#c2410c;border-color:#fdba74;',
    good: 'background:#ecfdf5;color:#047857;border-color:#6ee7b7;',
    neutral: 'background:#f8fafc;color:#475569;border-color:#cbd5e1;',
  };
  const recordRows = plan.top_records.length
    ? plan.top_records.map((record) => `
      <div style="padding:12px 14px;border:1px solid rgba(148,163,184,.22);border-radius:12px;background:#fff;">
        <div style="display:flex;justify-content:space-between;gap:10px;align-items:center;margin-bottom:4px;">
          <strong style="font-size:14px;color:var(--text);">${record.label}</strong>
          <span style="font-size:12px;font-weight:900;color:var(--primary);">${record.count}</span>
        </div>
        <div style="font-size:13px;line-height:1.45;color:var(--text-muted);">${record.detail}</div>
      </div>
    `).join('')
    : '<div style="font-size:14px;color:var(--text-muted);">No follow-up record groups are showing yet.</div>';
  const trigger = plan.upgrade_trigger;
  const triggerHtml = trigger ? `
      <div style="margin:0 0 12px;padding:12px 14px;border:1px solid;${toneStyle[trigger.tone] || toneStyle.neutral}border-radius:12px;">
        <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:inherit;opacity:.82;margin-bottom:4px;">${trigger.label}</div>
        <div style="font-size:15px;font-weight:900;color:inherit;margin-bottom:3px;">${trigger.status}</div>
        <div style="font-size:14px;line-height:1.5;color:inherit;opacity:.9;">${trigger.reason}</div>
        <div style="font-size:13px;line-height:1.45;color:inherit;opacity:.86;margin-top:5px;">${trigger.next_step}</div>
      </div>
    ` : '';
  const ctaHtml = plan.cta ? `<a href="${plan.cta.href}" class="btn btn-primary" data-pro-cta="record-action-plan" data-pro-action="${tier === 'free' ? 'unlock' : 'open'}">${plan.cta.label}</a>` : '';
  const html = `
    <div id="record-action-plan-card" style="margin-top:16px;padding:18px;border:1px solid var(--border);border-radius:var(--radius);background:linear-gradient(180deg,#fff 0%,#f8fafc 100%);box-shadow:0 8px 24px rgba(15,23,42,.06);">
      <div style="display:flex;justify-content:space-between;gap:12px;align-items:flex-start;flex-wrap:wrap;margin-bottom:12px;">
        <div>
          <div style="font-size:12px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-muted);margin-bottom:4px;">Record counts</div>
          <div style="font-size:20px;font-weight:800;color:var(--text);">${plan.title}</div>
          <div style="font-size:14px;color:var(--text-muted);margin-top:4px;max-width:760px;">${plan.summary}</div>
        </div>
        <div style="min-width:170px;padding:12px 14px;border:1px solid;${toneStyle[plan.tone] || toneStyle.neutral}border-radius:14px;text-align:center;">
          <div style="font-size:13px;font-weight:900;line-height:1.35;">${plan.status}</div>
          <div style="font-size:11px;color:inherit;opacity:.8;margin-top:6px;">${plan.total_records} follow-up record${plan.total_records === 1 ? '' : 's'}</div>
        </div>
      </div>
      <div style="margin:0 0 12px;padding:12px 14px;border:1px solid rgba(14,165,233,.24);border-radius:12px;background:#f0f9ff;">
        <div style="font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:#0369a1;margin-bottom:4px;">Clear first</div>
        <div style="font-size:15px;font-weight:800;color:var(--text);margin-bottom:3px;">${plan.next_step.label}</div>
        <div style="font-size:14px;line-height:1.5;color:var(--text-muted);">${plan.next_step.detail}</div>
      </div>
      ${triggerHtml}
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:10px;margin-bottom:${plan.cta ? '12px' : '0'};">${recordRows}</div>
      ${ctaHtml}
    </div>
  `;

  const mortgageCalc = document.getElementById('overview-mortgage-calc');
  if (existing) {
    existing.outerHTML = html;
    return;
  }
  (mortgageCalc || overviewGrid).insertAdjacentHTML('afterend', html);
}

function updateProLockCounts(counts) {
  const map = {
    'pro-count-ownership': { count: counts.ownership, label: 'deed records' },
    'pro-count-mortgages': { count: counts.mortgages, label: 'mortgage & lien filings' },
    'pro-count-sales': { count: counts.sales, label: 'sale records & comparables' },
    'pro-count-permits': { count: counts.permits, label: 'permits & filings' },
    'pro-count-violations': { count: counts.violations, label: 'violations' },
    'pro-count-complaints': { count: counts.complaints, label: 'complaints' },
    'pro-count-health': { count: counts.health, label: 'health records' },
  };

  for (const [id, { count, label }] of Object.entries(map)) {
    if (!count) continue;
    const el = document.getElementById(id);
    if (el) {
      el.textContent = `${count} ${label} found for this property`;
    }
  }
}

// ─── Core detail data (ACRIS + DOB + HPD) ───

async function loadDetailData() {
  if (detailData) return;

  const bbl = propertyData.bbl;
  ['ownership-content', 'mortgages-content', 'sales-content', 'permits-content', 'violations-content'].forEach(id => {
    const el = document.getElementById(id);
    if (el && !el.querySelector('.data-table') && !el.querySelector('.pro-lock'))
      el.innerHTML = '<div class="loading">Loading data from NYC Open Data...</div>';
  });

  try {
    detailData = await apiGet(`/api/property-detail?bbl=${bbl}`);
    renderOwnership(detailData.acris);
    renderMortgages(detailData.acris);
    renderSales(detailData.acris);
    renderPermits(detailData.dob);
    renderViolations(detailData.dob, detailData.hpd);
  } catch (e) {
    const errHtml = `<div class="loading">${e.error || 'Failed to load data'}</div>`;
    ['ownership-content', 'mortgages-content', 'sales-content', 'permits-content', 'violations-content'].forEach(id => {
      const el = document.getElementById(id);
      if (el && !el.querySelector('.data-table')) el.innerHTML = errHtml;
    });
  }
}

function renderOwnership(acris) {
  const el = document.getElementById('ownership-content');
  if (!el || !acris) return;

  const deeds = (acris.documents || []).filter(d =>
    ['DEED', 'DEEDO', 'DEED, RP'].includes((d.doc_type || '').toUpperCase())
  );

  if (!deeds.length) {
    const lot = propertyData?.bbl?.slice(6, 10);
    const isCondo = lot && parseInt(lot) >= 7500;
    const msg = isCondo
      ? 'No deed records found. This appears to be a condo unit — ACRIS records are typically filed under the parent building lot, not individual unit lots.'
      : 'No deed records found for this property.';
    el.innerHTML = `<p>${msg}</p><div id="hpd-reg-section"></div><div id="llc-portfolio-section"></div>`;
    setTabBadge('ownership', 0);
    return;
  }

  const deedRows = deeds.map(d => {
    const parties = (acris.parties || [])
      .filter(p => p.document_id === d.document_id)
      .map(p => `${p.name || ''} (${p.party_type === '1' ? 'Grantor' : 'Grantee'})`)
      .join(', ');
    return [formatDate(d.document_date), d.doc_type || '-', formatCurrency(d.document_amt), parties || '-'];
  });

  el.innerHTML = `<div id="ownership-deeds"><h3 class="section-header">Deed History</h3>`
    + tableHtml(['Date', 'Type', 'Amount', 'Parties'], deedRows, { col: 0, dir: 'desc' })
    + `</div>`
    + `<div id="hpd-reg-section"></div>`
    + `<div id="llc-portfolio-section"></div>`;
  setTabBadge('ownership', deeds.length);
}

// ─── 1A: Mortgages & Liens ───

function renderMortgages(acris) {
  const el = document.getElementById('mortgages-content');
  if (!el || !acris) return;

  const docs = acris.documents || [];
  const parties = acris.parties || [];

  const mortgages = docs.filter(d =>
    ['MTGE', 'SAT', 'ASST', 'AALR', 'AGMT'].includes((d.doc_type || '').toUpperCase())
  );
  const liens = docs.filter(d =>
    ['FEDL', 'LIEN', 'UCC1', 'UCC3', 'MCON', 'RPTT'].includes((d.doc_type || '').toUpperCase())
  );

  let html = '';

  // Mortgages section
  html += '<h3 class="section-header">Mortgages</h3>';
  if (mortgages.length) {
    const mtgRows = mortgages.map(d => {
      const lender = parties.filter(p => p.document_id === d.document_id && p.party_type === '2').map(p => p.name).join(', ');
      const borrower = parties.filter(p => p.document_id === d.document_id && p.party_type === '1').map(p => p.name).join(', ');
      const label = d.doc_type === 'MTGE' ? 'Mortgage' : d.doc_type === 'SAT' ? 'Satisfaction' : d.doc_type === 'ASST' ? 'Assignment' : d.doc_type === 'AALR' ? 'Assignment of Leases' : d.doc_type || '-';
      const partyStr = `${lender ? `Lender: ${lender}` : ''}${lender && borrower ? '; ' : ''}${borrower ? `Borrower: ${borrower}` : ''}${!lender && !borrower ? '-' : ''}`;
      return [formatDate(d.document_date), label, formatCurrency(d.document_amt), partyStr];
    });
    html += tableHtml(['Date', 'Type', 'Amount', 'Lender / Borrower'], mtgRows, { col: 0, dir: 'desc' });
  } else {
    html += '<p>No mortgage records found.</p>';
  }

  // Liens section
  html += '<h3 class="section-header">Liens & UCC Filings</h3>';
  if (liens.length) {
    const lienRows = liens.map(d => {
      const lienParties = parties.filter(p => p.document_id === d.document_id).map(p => `${p.name || ''} (${p.party_type === '1' ? 'Debtor' : 'Secured'})`).join(', ');
      const label = d.doc_type === 'FEDL' ? 'Federal Lien' : d.doc_type === 'LIEN' ? 'Lien' : d.doc_type === 'UCC1' ? 'UCC-1 Filing' : d.doc_type === 'UCC3' ? 'UCC-3 Amendment' : d.doc_type || '-';
      return [formatDate(d.document_date), label, formatCurrency(d.document_amt), lienParties || '-'];
    });
    html += tableHtml(['Date', 'Type', 'Amount', 'Parties'], lienRows, { col: 0, dir: 'desc' });
  } else {
    html += '<p>No lien records found.</p>';
  }

  el.innerHTML = html;
  setTabBadge('mortgages', mortgages.length + liens.length);
}

// ─── 1B: Enhanced Sales with Comparables ───

function renderSales(acris) {
  const el = document.getElementById('sales-content');
  if (!el || !acris) return;

  const sales = (acris.documents || []).filter(d =>
    ['DEED', 'DEEDO'].includes((d.doc_type || '').toUpperCase()) && parseFloat(d.document_amt) > 0
  );

  let html = '<h3 class="section-header">Sale History (ACRIS)</h3>';
  if (sales.length) {
    const saleRows = sales.map(d => [formatDate(d.document_date), formatCurrency(d.document_amt), d.doc_type || '-']);
    html += tableHtml(['Date', 'Sale Price', 'Document Type'], saleRows, { col: 0, dir: 'desc' });
  } else {
    html += '<p>No sale records found.</p>';
  }

  html += '<div id="comparable-sales-section"><div class="loading">Loading comparable sales...</div></div>';
  el.innerHTML = html;
  setTabBadge('sales', sales.length);
}

async function loadComparableSales() {
  if (salesLoaded) return;
  salesLoaded = true;

  const section = document.getElementById('comparable-sales-section');
  if (!section) return;

  try {
    const data = await apiGet(`/api/sales?bbl=${propertyData.bbl}`);
    const allComps = data.comparables || [];

    if (!allComps.length) {
      section.innerHTML = '<h3 class="section-header">Comparable Sales</h3><p>No comparable sales found in this area.</p>';
      return;
    }

    // Store all comps for filtering
    window.__compSalesData = allComps;

    // Subject property reference values for filter defaults
    const subjectSqft = propertyData.bldg_area || 0;
    const subjectYear = propertyData.year_built || 0;

    let html = '<h3 class="section-header">Comparable Sales (DOF Rolling Sales)</h3>';

    // Filter controls
    html += '<div class="comp-filters" id="comp-filters">';
    html += '<div class="comp-filter-group"><label>Sqft Range</label>';
    html += '<select id="comp-filter-sqft">';
    html += '<option value="all">All sizes</option>';
    if (subjectSqft > 0) {
      html += `<option value="10">&#177;10% of subject (${formatNumber(Math.round(subjectSqft * 0.9))}\u2013${formatNumber(Math.round(subjectSqft * 1.1))})</option>`;
      html += `<option value="20" selected>&#177;20% of subject (${formatNumber(Math.round(subjectSqft * 0.8))}\u2013${formatNumber(Math.round(subjectSqft * 1.2))})</option>`;
      html += `<option value="50">&#177;50% of subject (${formatNumber(Math.round(subjectSqft * 0.5))}\u2013${formatNumber(Math.round(subjectSqft * 1.5))})</option>`;
    }
    html += '</select></div>';

    html += '<div class="comp-filter-group"><label>Year Built</label>';
    html += '<select id="comp-filter-year">';
    html += '<option value="all">Any year</option>';
    if (subjectYear > 0) {
      html += `<option value="5">&#177;5 yrs (${subjectYear - 5}\u2013${subjectYear + 5})</option>`;
      html += `<option value="10" selected>&#177;10 yrs (${subjectYear - 10}\u2013${subjectYear + 10})</option>`;
      html += `<option value="20">&#177;20 yrs (${subjectYear - 20}\u2013${subjectYear + 20})</option>`;
    }
    html += '</select></div>';

    html += '<button class="comp-filter-reset" id="comp-filter-reset">Reset Filters</button>';
    html += '<span class="comp-filter-count" id="comp-filter-count"></span>';
    html += '</div>';

    // Stats row placeholder
    html += '<div id="comp-stats-row"></div>';

    // Trend chart placeholder
    html += '<div id="comp-trend-chart"></div>';

    // Table placeholder
    html += '<div id="comp-table-area"></div>';

    section.innerHTML = html;

    // Apply initial filter & render
    applyCompFilters();

    // Wire up filter change listeners
    const sqftSelect = document.getElementById('comp-filter-sqft');
    const yearSelect = document.getElementById('comp-filter-year');
    const resetBtn = document.getElementById('comp-filter-reset');
    if (sqftSelect) sqftSelect.addEventListener('change', applyCompFilters);
    if (yearSelect) yearSelect.addEventListener('change', applyCompFilters);
    if (resetBtn) resetBtn.addEventListener('click', () => {
      if (sqftSelect) sqftSelect.value = 'all';
      if (yearSelect) yearSelect.value = 'all';
      applyCompFilters();
    });

    // Update sales badge to include comparables
    const existingBadge = document.querySelector('.tab[data-tab="sales"] .tab-count');
    const acrisSales = existingBadge ? parseInt(existingBadge.textContent) || 0 : 0;
    setTabBadge('sales', acrisSales + allComps.length);
  } catch (e) {
    section.innerHTML = '<h3 class="section-header">Comparable Sales</h3><p>Could not load comparable sales data.</p>';
  }
}

function applyCompFilters() {
  const allComps = window.__compSalesData || [];
  const subjectSqft = propertyData.bldg_area || 0;
  const subjectYear = propertyData.year_built || 0;

  const sqftFilter = document.getElementById('comp-filter-sqft')?.value || 'all';
  const yearFilter = document.getElementById('comp-filter-year')?.value || 'all';

  let filtered = allComps;

  // Apply sqft filter
  if (sqftFilter !== 'all' && subjectSqft > 0) {
    const pct = parseInt(sqftFilter) / 100;
    const minSqft = subjectSqft * (1 - pct);
    const maxSqft = subjectSqft * (1 + pct);
    filtered = filtered.filter(c => {
      const sqft = c.sqft || 0;
      return sqft === 0 || (sqft >= minSqft && sqft <= maxSqft);
    });
  }

  // Apply year built filter
  if (yearFilter !== 'all' && subjectYear > 0) {
    const range = parseInt(yearFilter);
    const minYear = subjectYear - range;
    const maxYear = subjectYear + range;
    filtered = filtered.filter(c => {
      const yr = parseInt(c.year_built) || 0;
      return yr === 0 || (yr >= minYear && yr <= maxYear);
    });
  }

  // Update count
  const countEl = document.getElementById('comp-filter-count');
  if (countEl) {
    countEl.textContent = filtered.length === allComps.length
      ? `${allComps.length} comparables`
      : `${filtered.length} of ${allComps.length} comparables`;
  }

  // Render stats
  const statsRow = document.getElementById('comp-stats-row');
  if (statsRow) {
    const validPsf = filtered.filter(c => c.price_per_sqft > 0).map(c => c.price_per_sqft).sort((a, b) => a - b);
    const medianPsf = validPsf.length ? validPsf[Math.floor(validPsf.length / 2)] : null;
    const estValue = medianPsf && propertyData.bldg_area ? Math.round(medianPsf * propertyData.bldg_area) : null;

    let statsHtml = '';
    if (medianPsf || estValue) {
      statsHtml += '<div class="stat-row">';
      if (medianPsf) statsHtml += `<div class="stat-card"><div class="stat-value">${formatCurrency(medianPsf)}</div><div class="stat-label">Median $/sqft</div></div>`;
      if (estValue) statsHtml += `<div class="stat-card"><div class="stat-value">${formatCurrency(estValue)}</div><div class="stat-label">Est. Value (median x bldg area)</div></div>`;
      statsHtml += `<div class="stat-card"><div class="stat-value">${filtered.length}</div><div class="stat-label">Comparables</div></div>`;
      statsHtml += '</div>';
    }
    statsRow.innerHTML = statsHtml;
  }

  // Render $/sqft trend chart
  renderCompTrendChart(filtered);

  // Render table
  const tableArea = document.getElementById('comp-table-area');
  if (tableArea) {
    const compRows = filtered.slice(0, 25).map(c => [
      c.address || '-', formatDate(c.sale_date), formatCurrency(c.sale_price),
      c.sqft ? formatNumber(c.sqft) : '-', c.price_per_sqft ? formatCurrency(c.price_per_sqft) : '-',
      c.year_built || '-', c.building_class || '-',
    ]);
    if (compRows.length) {
      tableArea.innerHTML = tableHtml(['Address', 'Date', 'Price', 'Sqft', '$/Sqft', 'Year Built', 'Class'], compRows, { col: 1, dir: 'desc' });
    } else {
      tableArea.innerHTML = '<p>No comparables match the current filters. Try widening the range.</p>';
    }
  }
}

function renderCompTrendChart(comps) {
  const container = document.getElementById('comp-trend-chart');
  if (!container) return;

  // Get comps with valid $/sqft and sale dates, sorted by date ascending
  const valid = comps
    .filter(c => c.price_per_sqft > 0 && c.sale_date)
    .map(c => ({ date: new Date(c.sale_date), psf: c.price_per_sqft }))
    .sort((a, b) => a.date - b.date);

  if (valid.length < 2) {
    container.innerHTML = '';
    return;
  }

  // Group by month and calculate average $/sqft
  const monthly = {};
  for (const v of valid) {
    const key = `${v.date.getFullYear()}-${String(v.date.getMonth() + 1).padStart(2, '0')}`;
    if (!monthly[key]) monthly[key] = { total: 0, count: 0 };
    monthly[key].total += v.psf;
    monthly[key].count++;
  }

  const points = Object.entries(monthly)
    .map(([key, val]) => ({ label: key, avg: Math.round(val.total / val.count) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (points.length < 2) {
    container.innerHTML = '';
    return;
  }

  // SVG line chart dimensions
  const W = 600;
  const H = 180;
  const PAD_L = 60;
  const PAD_R = 20;
  const PAD_T = 20;
  const PAD_B = 40;
  const chartW = W - PAD_L - PAD_R;
  const chartH = H - PAD_T - PAD_B;

  const maxPsf = Math.max(...points.map(p => p.avg));
  const minPsf = Math.min(...points.map(p => p.avg));
  const range = maxPsf - minPsf || 1;

  // Scale points to chart area
  const coords = points.map((p, i) => {
    const x = PAD_L + (i / (points.length - 1)) * chartW;
    const y = PAD_T + chartH - ((p.avg - minPsf) / range) * chartH;
    return { x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, ...p };
  });

  // Build polyline path
  const polyline = coords.map(c => `${c.x},${c.y}`).join(' ');

  // Area fill path (polyline + bottom corners)
  const areaPath = `M${coords[0].x},${PAD_T + chartH} L${polyline} L${coords[coords.length - 1].x},${PAD_T + chartH} Z`;

  let svg = '<div class="trend-chart-wrap"><h4>$/Sqft Trend (Monthly Average)</h4>';
  svg += `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">`;

  // Grid lines (3 horizontal)
  for (let i = 0; i <= 2; i++) {
    const y = PAD_T + (chartH / 2) * i;
    const val = Math.round(maxPsf - (range / 2) * i);
    svg += `<line x1="${PAD_L}" y1="${y}" x2="${W - PAD_R}" y2="${y}" stroke="#e2e8f0" stroke-width="1"/>`;
    svg += `<text x="${PAD_L - 8}" y="${y + 4}" text-anchor="end" fill="#94a3b8" font-size="10" font-family="Inter,sans-serif">$${val}</text>`;
  }

  // X-axis baseline
  svg += `<line x1="${PAD_L}" y1="${PAD_T + chartH}" x2="${W - PAD_R}" y2="${PAD_T + chartH}" stroke="#e2e8f0" stroke-width="1"/>`;

  // X-axis labels (show every label if few points, otherwise skip)
  const labelStep = points.length <= 8 ? 1 : Math.ceil(points.length / 6);
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  for (let i = 0; i < coords.length; i += labelStep) {
    const c = coords[i];
    const parts = c.label.split('-');
    const shortLabel = `${monthNames[parseInt(parts[1]) - 1]} '${parts[0].slice(2)}`;
    svg += `<text x="${c.x}" y="${H - 8}" text-anchor="middle" fill="#94a3b8" font-size="10" font-family="Inter,sans-serif">${shortLabel}</text>`;
  }

  // Area fill
  svg += `<path d="${areaPath}" fill="#dbeafe" opacity="0.5"/>`;

  // Line
  svg += `<polyline points="${polyline}" fill="none" stroke="#1a56db" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;

  // Data points with tooltips
  for (const c of coords) {
    svg += `<circle cx="${c.x}" cy="${c.y}" r="3.5" fill="#1a56db" stroke="#fff" stroke-width="1.5"><title>${c.label}: $${c.avg}/sqft</title></circle>`;
  }

  svg += '</svg></div>';
  container.innerHTML = svg;
}

// ─── Permits ───

function renderPermits(dob) {
  const el = document.getElementById('permits-content');
  if (!el || !dob) return;

  const permits = dob.permits || [];
  let html = '<div id="dob-jobs-section"></div><div id="cof-occ-section"></div>';

  html += '<h3 class="section-header">DOB Permits</h3>';
  if (permits.length) {
    const permitRows = permits.slice(0, 50).map(p => [
      formatDate(p.issuance_date), p.permit_type || '-', p.permit_status || '-',
      p.job_description || '-',
    ]);
    html += tableHtml(['Date', 'Type', 'Status', 'Description'], permitRows, { col: 0, dir: 'desc' });
  } else {
    html += '<p>No DOB permits found.</p>';
  }

  el.innerHTML = html;
  setTabBadge('permits', permits.length);
}

// ─── Violations (DOB + HPD + ECB) ───

function violationSeverity(v) {
  // HPD classes: C = immediately hazardous, B = hazardous, A = non-hazardous, I = info
  const cls = (v.type || '').toUpperCase().trim();
  if (v.source === 'HPD') {
    if (cls === 'C') return { level: 0, label: 'Critical', color: '#dc2626' };
    if (cls === 'B') return { level: 1, label: 'Hazardous', color: '#d97706' };
    if (cls === 'A') return { level: 2, label: 'Non-Hazardous', color: '#059669' };
    return { level: 3, label: cls || 'Info', color: '#6b7280' };
  }
  // DOB: use violation_type keywords as severity hints
  const t = cls.toLowerCase();
  if (t.includes('imm') || t.includes('unsafe') || t.includes('hazard') || t.includes('stop work'))
    return { level: 0, label: 'Critical', color: '#dc2626' };
  if (t.includes('active') || t.includes('fail'))
    return { level: 1, label: 'Hazardous', color: '#d97706' };
  return { level: 2, label: 'Standard', color: '#059669' };
}

function violationIsOpen(v) {
  const s = (v.status || '').toLowerCase();
  return !s.includes('dismiss') && !s.includes('closed') && !s.includes('resolved')
    && !s.includes('complied') && !s.includes('vacated') && !s.includes('cure');
}

function renderViolations(dob, hpd) {
  const el = document.getElementById('violations-content');
  if (!el) return;

  const dobV = (dob?.violations || []).map(v => ({
    date: v.issue_date,
    source: 'DOB',
    type: v.violation_type || '-',
    status: v.disposition_comments || '-',
    desc: v.violation_description || '-',
  }));

  const hpdV = (hpd?.violations || []).map(v => ({
    date: v.inspectiondate,
    source: 'HPD',
    type: v.class || '-',
    status: v.currentstatus || '-',
    desc: v.novdescription || '-',
  }));

  const all = [...dobV, ...hpdV];

  // Classify each violation
  all.forEach(v => {
    v.sev = violationSeverity(v);
    v.open = violationIsOpen(v);
  });

  // Sort: open first, then by severity (critical first), then by date descending
  all.sort((a, b) => {
    if (a.open !== b.open) return a.open ? -1 : 1;
    if (a.sev.level !== b.sev.level) return a.sev.level - b.sev.level;
    return (b.date || '').localeCompare(a.date || '');
  });

  let html = '<h3 class="section-header">DOB & HPD Violations</h3>';

  if (all.length) {
    // Summary stats
    const openCount = all.filter(v => v.open).length;
    const closedCount = all.length - openCount;
    const critCount = all.filter(v => v.sev.level === 0).length;
    const hazCount = all.filter(v => v.sev.level === 1).length;

    html += '<div class="stat-row">';
    html += `<div class="stat-card"><div class="stat-value">${all.length}</div><div class="stat-label">Total</div></div>`;
    html += `<div class="stat-card"><div class="stat-value" style="color:#dc2626">${openCount}</div><div class="stat-label">Open</div></div>`;
    html += `<div class="stat-card"><div class="stat-value" style="color:#059669">${closedCount}</div><div class="stat-label">Closed</div></div>`;
    if (critCount > 0) html += `<div class="stat-card"><div class="stat-value" style="color:#dc2626">${critCount}</div><div class="stat-label">Critical</div></div>`;
    if (hazCount > 0) html += `<div class="stat-card"><div class="stat-value" style="color:#d97706">${hazCount}</div><div class="stat-label">Hazardous</div></div>`;
    html += '</div>';

    if (openCount > 0) {
      html += `<div class="alert-box alert-red">${openCount} open violation${openCount > 1 ? 's' : ''} — sorted to top</div>`;
    }

    const vRows = all.slice(0, 50).map(v => {
      const sevBadge = `<span style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;color:#fff;background:${v.sev.color}">${v.sev.label}</span>`;
      const statusBadge = v.open
        ? `<span style="display:inline-block;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:600;color:#dc2626;background:#fef2f2;border:1px solid #fecaca">Open</span> ${v.status}`
        : v.status;
      return [formatDate(v.date), v.source, sevBadge, statusBadge, v.desc || '-'];
    });
    html += tableHtml(['Date', 'Source', 'Severity', 'Status', 'Description'], vRows, { col: 0, dir: 'desc' });
  } else {
    html += '<p>No violations found.</p>';
  }

  html += '<div id="ecb-section"></div>';
  el.innerHTML = html;
  setTabBadge('violations', all.length);
}

// ─── 1C: ECB Violations with Fines ───

async function loadECB() {
  if (ecbLoaded) return;
  ecbLoaded = true;

  const section = document.getElementById('ecb-section');
  if (!section) return;
  section.innerHTML = '<div class="loading">Loading ECB data...</div>';

  try {
    const data = await apiGet(`/api/ecb?bbl=${propertyData.bbl}`);
    const ecb = data.ecb || [];

    if (!ecb.length) {
      section.innerHTML = '<h3 class="section-header">ECB Violations & Fines</h3><p>No ECB violation records found.</p>';
      return;
    }

    // Calculate totals — NYC data has typo: "penality_imposed"
    const totalPenalty = ecb.reduce((sum, v) => sum + (parseFloat(v.penality_imposed) || 0), 0);
    const totalPaid = ecb.reduce((sum, v) => sum + (parseFloat(v.amount_paid) || 0), 0);
    const totalBalance = ecb.reduce((sum, v) => sum + (parseFloat(v.balance_due) || 0), 0);

    let html = '<h3 class="section-header">ECB Violations & Fines</h3>';

    if (totalBalance > 0) {
      html += `<div class="alert-box alert-red">Outstanding ECB balance: ${formatCurrency(totalBalance)}</div>`;
    }

    html += '<div class="stat-row">';
    html += `<div class="stat-card"><div class="stat-value">${ecb.length}</div><div class="stat-label">Violations</div></div>`;
    html += `<div class="stat-card"><div class="stat-value">${formatCurrency(totalPenalty)}</div><div class="stat-label">Total Penalties</div></div>`;
    html += `<div class="stat-card"><div class="stat-value">${formatCurrency(totalPaid)}</div><div class="stat-label">Amount Paid</div></div>`;
    html += `<div class="stat-card"><div class="stat-value">${formatCurrency(totalBalance)}</div><div class="stat-label">Balance Due</div></div>`;
    html += '</div>';

    const ecbRows = ecb.slice(0, 50).map(v => [
      formatDate(v.issue_date), v.violation_type || '-',
      v.ecb_violation_status || v.hearing_status || '-',
      formatCurrency(v.penality_imposed), formatCurrency(v.amount_paid), formatCurrency(v.balance_due),
    ]);
    html += tableHtml(['Date', 'Type', 'Status', 'Penalty', 'Paid', 'Balance'], ecbRows, { col: 0, dir: 'desc' });

    section.innerHTML = html;
  } catch (e) {
    section.innerHTML = '<h3 class="section-header">ECB Violations & Fines</h3><p>Could not load ECB data.</p>';
  }
}

// ─── 1D: HPD Building Registrations ───

async function loadHPDRegistrations() {
  if (hpdRegLoaded) return;
  hpdRegLoaded = true;

  const section = document.getElementById('hpd-reg-section');
  if (!section) return;
  section.innerHTML = '<div class="loading">Loading HPD registration...</div>';

  try {
    const data = await apiGet(`/api/hpd-reg?bbl=${propertyData.bbl}`);
    const regs = data.registrations || [];

    if (!regs.length) {
      section.innerHTML = '<h3 class="section-header">HPD Building Registration</h3><p>No HPD registration found (may not be a registered multifamily building).</p>';
      return;
    }

    const regRows = regs.map(r => [
      r.type || r.contactdescription || '-',
      [r.firstname, r.lastname].filter(Boolean).join(' ') || r.corporationname || '-',
      [r.businesshousenumber, r.businessstreetname, r.businesscity, r.businessstate, r.businesszip].filter(Boolean).join(', ') || '-',
    ]);
    let html = '<h3 class="section-header">HPD Building Registration</h3>';
    html += tableHtml(['Type', 'Name', 'Business Address'], regRows);

    section.innerHTML = html;
  } catch (e) {
    section.innerHTML = '<h3 class="section-header">HPD Building Registration</h3><p>Could not load HPD registration data.</p>';
  }
}

// ─── 1C: LLC Portfolio ───

const LLC_PATTERNS = /\bLLC\b|\bCORP\b|\bINC\b|\bTRUST\b|\bL\.?P\.?\b|\bLTD\b|\bCO\b|\bASSOC\b|\bHOLDINGS?\b|\bGROUP\b|\bPARTNERS?\b|\bENTERPRISES?\b|\bPROPERT(Y|IES)\b|\bREALTY\b|\bDEVELOPMENT\b|\bMGMT\b|\bMGT\b|\bMANAGEMENT\b/i;

function isLikelyLLC(name) {
  if (!name || name === '-') return false;
  return LLC_PATTERNS.test(name);
}

async function loadLLCPortfolio() {
  if (llcPortfolioLoaded) return;
  llcPortfolioLoaded = true;

  const section = document.getElementById('llc-portfolio-section');
  if (!section) return;

  // Check if owner name looks like an LLC/entity
  const ownerName = propertyData?.owner_name;
  if (!ownerName || !isLikelyLLC(ownerName)) return;

  section.innerHTML = '<div class="loading">Searching for other properties owned by this entity...</div>';

  try {
    const data = await apiGet(`/api/llc-portfolio?bbl=${propertyData.bbl}&owner=${encodeURIComponent(ownerName)}`);
    const properties = data.properties || [];

    if (!properties.length) {
      section.innerHTML = '<h3 class="section-header">Owner Portfolio</h3>'
        + '<p>No other properties found for this owner in ACRIS records.</p>';
      return;
    }

    const rows = properties.map(p => {
      const addrLink = `<a href="/property?bbl=${p.bbl}">${p.address || p.bbl}</a>`;
      return [
        addrLink,
        formatBBL(p.bbl),
        p.bldg_class || '-',
        p.units_total || '-',
        p.year_built || '-',
        p.bldg_area ? formatSqft(p.bldg_area) : '-',
      ];
    });

    let html = `<h3 class="section-header">Owner Portfolio</h3>`;
    html += `<p style="margin-bottom:8px;color:var(--text-muted);font-size:13px">${data.total} other propert${data.total === 1 ? 'y' : 'ies'} found owned by <strong>${data.owner}</strong></p>`;
    html += tableHtml(['Address', 'BBL', 'Class', 'Units', 'Year Built', 'Building Area'], rows, { col: 0, dir: 'asc' });

    section.innerHTML = html;
  } catch (e) {
    // If 403 (not pro), just hide the section silently
    if (e.status === 403 || e.status === 401) {
      section.innerHTML = '';
      return;
    }
    section.innerHTML = '<h3 class="section-header">Owner Portfolio</h3>'
      + '<p>Could not load portfolio data.</p>';
  }
}

// ─── 2A: Tax Assessment History ───

async function loadTaxHistory() {
  if (taxHistoryLoaded) return;
  taxHistoryLoaded = true;

  const taxGrid = document.getElementById('tax-grid');
  if (!taxGrid) return;

  // Append sections after the grid
  let histSection = document.getElementById('tax-history-section');
  if (!histSection) {
    histSection = document.createElement('div');
    histSection.id = 'tax-history-section';
    taxGrid.parentElement.appendChild(histSection);
  }
  histSection.innerHTML = '<div class="loading">Loading tax history...</div>';

  try {
    const data = await apiGet(`/api/tax-history?bbl=${propertyData.bbl}`);
    const history = data.history || [];

    if (!history.length) {
      histSection.innerHTML = '<h3 class="section-header">Assessment History</h3><p>No assessment history available.</p>';
      return;
    }

    // Bar chart of market values
    const years = history.slice(0, 10).reverse();
    const maxVal = Math.max(...years.map(y => parseFloat(y.curmkttot) || 0));

    let html = '<h3 class="section-header">Assessment History</h3>';

    if (maxVal > 0) {
      html += '<div class="bar-chart">';
      for (const y of years) {
        const val = parseFloat(y.curmkttot) || 0;
        const pct = maxVal > 0 ? Math.round((val / maxVal) * 100) : 0;
        html += `<div class="bar" style="height:${Math.max(pct, 2)}%" title="${y.year}: ${formatCurrency(val)}"><span class="bar-label">${y.year || ''}</span></div>`;
      }
      html += '</div><div style="margin-bottom:16px"></div>';
    }

    const taxRows = history.map(h => [
      h.year || '-', formatCurrency(h.curmkttot), formatCurrency(h.curacttot),
      formatCurrency(h.curactextot), formatCurrency(h.curtxbtot),
    ]);
    html += tableHtml(['Year', 'Market Value', 'Assessed Value', 'Exempt Value', 'Taxable Value'], taxRows, { col: 0, dir: 'desc' });

    // Show exemptions if any
    const exempted = history.filter(h => parseFloat(h.curactextot) > 0);
    if (exempted.length) {
      html += `<div class="alert-box alert-blue">This property has tax exemptions in ${exempted.length} of the last ${history.length} years.</div>`;
    }

    histSection.innerHTML = html;
  } catch (e) {
    histSection.innerHTML = '<h3 class="section-header">Assessment History</h3><p>Could not load tax history.</p>';
  }
}

// ─── 2B: DOB Job Filings ───

async function loadDOBJobs() {
  if (dobJobsLoaded) return;
  dobJobsLoaded = true;

  const section = document.getElementById('dob-jobs-section');
  if (!section) return;
  section.innerHTML = '<div class="loading">Loading job filings...</div>';

  try {
    const data = await apiGet(`/api/dob-jobs?bbl=${propertyData.bbl}`);
    const jobs = data.jobs || [];

    if (!jobs.length) {
      section.innerHTML = '';
      return;
    }

    const jobRows = jobs.slice(0, 30).map(j => {
      const typeLabel = j.job_type === 'NB' ? 'New Building' : j.job_type === 'A1' ? 'Alteration Type 1' : j.job_type === 'A2' ? 'Alteration Type 2' : j.job_type === 'A3' ? 'Alteration Type 3' : j.job_type === 'DM' ? 'Demolition' : j.job_type || '-';
      return [j.job_filing_number || '-', typeLabel, j.filing_status || '-', formatDate(j.filing_date), formatCurrency(j.initial_cost), j.proposed_dwelling_units || j.existing_dwelling_units || '-'];
    });
    let html = '<h3 class="section-header">DOB Job Filings</h3>';
    html += tableHtml(['Job #', 'Type', 'Status', 'Filed', 'Est. Cost', 'Units'], jobRows, { col: 3, dir: 'desc' });

    section.innerHTML = html;
  } catch (e) {
    section.innerHTML = '';
  }
}

// ─── 2C: Certificate of Occupancy ───

async function loadCofOcc() {
  if (cofOccLoaded) return;
  cofOccLoaded = true;

  const section = document.getElementById('cof-occ-section');
  if (!section) return;
  section.innerHTML = '<div class="loading">Loading C of O...</div>';

  try {
    const data = await apiGet(`/api/cof-occ?bbl=${propertyData.bbl}`);
    const certs = data.certificates || [];

    if (!certs.length) {
      section.innerHTML = '';
      return;
    }

    const cofRows = certs.slice(0, 20).map(c => [
      formatDate(c.c_o_issue_date), c.job_type || '-', c.job_number || '-',
      c.issue_type || c.filing_status_raw || '-',
    ]);
    let html = '<h3 class="section-header">Certificate of Occupancy</h3>';
    html += tableHtml(['Issue Date', 'Job Type', 'Job #', 'Status'], cofRows, { col: 0, dir: 'desc' });

    section.innerHTML = html;
  } catch (e) {
    section.innerHTML = '';
  }
}

// ─── 2D: 311 Complaints ───

async function load311Complaints() {
  if (complaintsLoaded) return;
  complaintsLoaded = true;

  const el = document.getElementById('complaints-content');
  if (!el) return;
  el.innerHTML = '<div class="loading">Loading 311 complaints...</div>';

  try {
    const data = await apiGet(`/api/311?bbl=${propertyData.bbl}`);
    const complaints = data.complaints || [];
    const typeCounts = data.typeCounts || {};

    if (!complaints.length) {
      el.innerHTML = '<p>No 311 complaints found for this property.</p>';
      return;
    }

    // Summary by type
    const sortedTypes = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

    let html = '<h3 class="section-header">Complaint Summary</h3>';
    html += '<div class="stat-row">';
    html += `<div class="stat-card"><div class="stat-value">${complaints.length}</div><div class="stat-label">Total Complaints</div></div>`;
    if (sortedTypes[0]) html += `<div class="stat-card"><div class="stat-value">${sortedTypes[0][1]}</div><div class="stat-label">${sortedTypes[0][0]}</div></div>`;
    if (sortedTypes[1]) html += `<div class="stat-card"><div class="stat-value">${sortedTypes[1][1]}</div><div class="stat-label">${sortedTypes[1][0]}</div></div>`;
    html += '</div>';

    // Type breakdown
    if (sortedTypes.length > 2) {
      html += '<div style="margin-bottom:16px">';
      for (const [type, count] of sortedTypes.slice(0, 10)) {
        const pct = Math.round((count / complaints.length) * 100);
        html += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;font-size:13px">
          <div style="width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${type}">${type}</div>
          <div style="flex:1;height:14px;background:#e2e8f0;border-radius:3px;overflow:hidden"><div style="width:${pct}%;height:100%;background:var(--primary);border-radius:3px"></div></div>
          <div style="width:40px;text-align:right;color:var(--text-muted)">${count}</div>
        </div>`;
      }
      html += '</div>';
    }

    // Complaint table
    html += '<h3 class="section-header">Recent Complaints</h3>';
    const cRows = complaints.slice(0, 50).map(c => [
      formatDate(c.created_date), c.complaint_type || '-', c.status || '-',
      c.resolution_description || '-',
    ]);
    html += tableHtml(['Date', 'Type', 'Status', 'Resolution'], cRows, { col: 0, dir: 'desc' });

    el.innerHTML = html;
    setTabBadge('complaints', complaints.length);
  } catch (e) {
    el.innerHTML = `<p>${e.error || 'Failed to load 311 complaints'}</p>`;
  }
}

// ─── 2G: Tax Balance ───

async function loadTaxBalance() {
  if (taxBalanceLoaded) return;
  taxBalanceLoaded = true;

  const taxGrid = document.getElementById('tax-grid');
  if (!taxGrid) return;

  let balSection = document.getElementById('tax-balance-section');
  if (!balSection) {
    balSection = document.createElement('div');
    balSection.id = 'tax-balance-section';
    taxGrid.parentElement.appendChild(balSection);
  }

  try {
    const data = await apiGet(`/api/tax-balance?bbl=${propertyData.bbl}`);
    const balance = data.balance || [];

    if (!balance.length) return;

    const latest = balance[0];
    const outstanding = parseFloat(latest.sum_bal) || parseFloat(latest.sum_liab) || 0;

    if (outstanding > 0) {
      balSection.innerHTML = `
        <h3 class="section-header">Current Tax Balance</h3>
        <div class="alert-box alert-red">Outstanding tax balance: ${formatCurrency(outstanding)}</div>`;
    } else {
      balSection.innerHTML = `
        <h3 class="section-header">Current Tax Balance</h3>
        <div class="alert-box alert-green">No outstanding tax balance.</div>`;
    }
  } catch (e) {
    // Silently fail — not critical
  }
}

// ─── 2H: Health & Inspections ───

async function loadHealthData() {
  if (healthDataLoaded) return;
  healthDataLoaded = true;

  const el = document.getElementById('health-content');
  if (!el) return;
  el.innerHTML = '<div class="loading">Loading health & inspection data...</div>';

  try {
    const data = await apiGet(`/api/health-data?bbl=${propertyData.bbl}`);
    let html = '';
    html += renderRestaurantSection(data.restaurants);
    html += renderRodentSection(data.rodent);
    html += renderBedbugSection(data.bedbug);

    if (!html) {
      el.innerHTML = '<p>No health inspection records found for this property.</p>';
      setTabBadge('health', 0);
      return;
    }
    el.innerHTML = html;
    const totalHealth = (data.restaurants?.count || 0)
      + (data.rodent?.totalInspections || 0)
      + (data.bedbug?.totalFilings || 0);
    setTabBadge('health', totalHealth);
  } catch (e) {
    el.innerHTML = `<p>${e.error || 'Failed to load health data'}</p>`;
  }
}

function gradeAlertClass(grade) {
  if (grade === 'A') return 'alert-green';
  if (grade === 'B') return 'alert-yellow';
  return 'alert-red';
}

function renderRestaurantSection(restaurants) {
  if (!restaurants || !restaurants.count) return '';

  let html = '<h3 class="section-header">Restaurant Inspections (DOHMH)</h3>';

  for (const est of restaurants.establishments) {
    const gradeLabel = est.latestGrade || 'N/A';
    const scoreLabel = est.latestScore != null ? ` (Score: ${est.latestScore})` : '';
    const dateLabel = est.latestDate ? ` — Last inspected ${formatDate(est.latestDate)}` : '';
    const alertClass = est.latestGrade ? gradeAlertClass(est.latestGrade) : 'alert-yellow';

    html += `<div class="alert-box ${alertClass}">
      <strong>${est.name}</strong> (${est.cuisine}) — Grade ${gradeLabel}${scoreLabel}${dateLabel}
    </div>`;

    if (est.gradeHistory.length > 1) {
      html += '<div style="margin-bottom:8px;font-size:13px;color:var(--text-muted)">Grade history: '
        + est.gradeHistory.map(g => `${g.grade} (${formatDate(g.date)})`).join(' → ')
        + '</div>';
    }

    if (est.violations.length) {
      html += tableHtml(
        ['Date', 'Code', 'Description', 'Critical'],
        est.violations.slice(0, 10).map(v => [
          formatDate(v.date), v.code || '-',
          v.description || '-',
          v.critical ? 'Yes' : 'No',
        ]),
        { col: 0, dir: 'desc' }
      );
    }
  }

  return html;
}

function renderRodentSection(rodent) {
  if (!rodent) return '';

  let html = '<h3 class="section-header">Rodent Inspections</h3>';

  const isActive = (rodent.latestResult || '').toLowerCase().includes('rat activity');
  if (isActive) {
    html += `<div class="alert-box alert-red">Active rodent activity detected (${formatDate(rodent.latestDate)})</div>`;
  } else if (rodent.latestResult === 'Passed') {
    html += `<div class="alert-box alert-green">Passed — no rodent activity (${formatDate(rodent.latestDate)})</div>`;
  } else {
    html += `<div class="alert-box alert-yellow">Latest result: ${rodent.latestResult} (${formatDate(rodent.latestDate)})</div>`;
  }

  html += '<div class="stat-row">';
  html += `<div class="stat-card"><div class="stat-value">${rodent.totalInspections}</div><div class="stat-label">Total Inspections</div></div>`;
  if (rodent.passRate != null) {
    html += `<div class="stat-card"><div class="stat-value">${rodent.passRate}%</div><div class="stat-label">Pass Rate</div></div>`;
  }
  html += '</div>';

  if (rodent.inspections.length) {
    html += tableHtml(
      ['Date', 'Type', 'Result'],
      rodent.inspections.map(i => [formatDate(i.date), i.type, i.result]),
      { col: 0, dir: 'desc' }
    );
  }

  return html;
}

function renderBedbugSection(bedbug) {
  if (!bedbug) return '';

  let html = '<h3 class="section-header">Bedbug Reporting</h3>';

  if (bedbug.latestInfested > 0) {
    html += `<div class="alert-box alert-red">Bedbug infestation reported: ${bedbug.latestInfested} of ${bedbug.totalUnits} units (${bedbug.infestationRate}%)</div>`;
  } else {
    html += `<div class="alert-box alert-green">No current bedbug infestations reported</div>`;
  }

  html += '<div class="stat-row">';
  html += `<div class="stat-card"><div class="stat-value">${bedbug.totalFilings}</div><div class="stat-label">Total Filings</div></div>`;
  html += `<div class="stat-card"><div class="stat-value">${bedbug.latestInfested}</div><div class="stat-label">Infested Units</div></div>`;
  html += `<div class="stat-card"><div class="stat-value">${bedbug.totalUnits}</div><div class="stat-label">Total Units</div></div>`;
  html += `<div class="stat-card"><div class="stat-value">${bedbug.infestationRate}%</div><div class="stat-label">Infestation Rate</div></div>`;
  html += '</div>';

  if (bedbug.filings.length) {
    html += tableHtml(
      ['Date', 'Infested', 'Eradicated', 'Re-infested', 'Total Units'],
      bedbug.filings.map(f => [
        formatDate(f.date), f.infested, f.eradicated, f.reinfested, f.totalUnits,
      ]),
      { col: 0, dir: 'desc' }
    );
  }

  return html;
}

// ─── 3A: Property Health Score ───

// ─── Export functionality ───

function getExportData() {
  if (!propertyData) return null;
  const p = propertyData;
  const d = devData || {};
  const z = zoningData || {};
  return {
    address: p.address, borough: p.boroughName || boroughName(p.borough), bbl: p.bbl,
    zipcode: p.zipcode, owner: p.owner_name, building_class: p.bldg_class,
    land_use: landUseLabel(p.land_use), year_built: p.year_built,
    lot_area: p.lot_area, lot_front: p.lot_front, lot_depth: p.lot_depth,
    bldg_area: p.bldg_area, bldg_front: p.bldg_front, bldg_depth: p.bldg_depth,
    floors: p.num_floors, total_units: p.units_total, res_units: p.units_res,
    res_area: p.res_area, com_area: p.com_area,
    zoning: p.zoning_dist1, built_far: d.built_far || p.far_built,
    max_res_far: d.official_res_far, max_buildable: d.max_buildable_sqft,
    unused_dev_rights: d.unused_dev_rights,
    assessed_total: p.assessed_total, assessed_land: p.assessed_land,
  };
}

window.exportPropertyCSV = function () {
  const data = getExportData();
  if (!data) return;
  const rows = [['Field', 'Value']];
  for (const [key, val] of Object.entries(data)) {
    rows.push([key.replace(/_/g, ' '), val != null ? String(val) : '']);
  }
  const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  downloadFile(csv, `property-${data.bbl}.csv`, 'text/csv');
  if (typeof window.trackEvent === 'function') window.trackEvent('export', 'csv', String(rows.length));
  if (window.showToast) window.showToast('CSV exported', 'success');
};

window.exportPropertyJSON = function () {
  const data = getExportData();
  if (!data) return;
  const json = JSON.stringify(data, null, 2);
  downloadFile(json, `property-${data.bbl}.json`, 'application/json');
  if (typeof window.trackEvent === 'function') window.trackEvent('export', 'json', '1');
  if (window.showToast) window.showToast('JSON exported', 'success');
};

function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── 3A: Property Health Score ───

async function loadHealthScore() {
  if (healthScoreLoaded) return;
  healthScoreLoaded = true;

  try {
    const data = await apiGet(`/api/health-score?bbl=${propertyData.bbl}`);
    const score = data.score;
    const breakdown = data.breakdown || [];

    if (score == null) return;

    // Color based on score
    let colorClass;
    if (score >= 80) colorClass = 'health-green';
    else if (score >= 60) colorClass = 'health-blue';
    else if (score >= 40) colorClass = 'health-yellow';
    else colorClass = 'health-red';

    // Badge in hero
    const badge = document.getElementById('health-score-badge');
    if (badge) {
      badge.style.display = 'block';
      badge.innerHTML = `<div class="health-badge ${colorClass}" title="Property Health Score">${score}</div>`;
      if (typeof window.trackEvent === 'function') window.trackEvent('health', 'view', String(score));
    }

    // Add breakdown to overview
    const overviewGrid = document.getElementById('overview-grid');
    if (overviewGrid && breakdown.length) {
      const breakdownEl = document.createElement('div');
      breakdownEl.style.cssText = 'grid-column: 1 / -1; margin-top: 8px;';
      breakdownEl.innerHTML = `
        <h3 class="section-header">Property Health Score: ${score}/100</h3>
        ${breakdown.map(b => `<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:13px;border-bottom:1px solid var(--border)">
          <span>${b.reason}</span>
          <span style="color:${b.points < 0 ? '#dc2626' : '#16a34a'};font-weight:600">${b.points > 0 ? '+' : ''}${b.points}</span>
        </div>`).join('')}`;
      overviewGrid.appendChild(breakdownEl);
    }
  } catch (e) {
    // Silently fail
  }
}

// ─── NEW: Investment Property Analyzer ───

let investmentAnalysisLoaded = false;

async function loadInvestmentAnalysis() {
  if (investmentAnalysisLoaded || userTier === 'free') return;
  investmentAnalysisLoaded = true;

  const container = document.getElementById('investment-analysis');
  if (!container) return;

  // Show Pro feature
  if (userTier !== 'pro' && userTier !== 'enterprise') {
    container.innerHTML = `
      <div class="pro-feature">
        <h3>🔬 Investment Analysis</h3>
        <p>Calculate rental yield, cash flow, and cap rates. Available in Pro tier.</p>
        <a href="/pricing" class="btn-primary">Upgrade to Pro</a>
      </div>
    `;
    return;
  }

  // Show investment calculator form
  container.innerHTML = `
    <div class="section-header-with-action">
      <h3>🔬 Investment Analysis</h3>
      <button onclick="calculateInvestment()" class="btn-secondary" id="calc-investment-btn">Calculate</button>
    </div>
    
    <div class="investment-calculator">
      <div class="calc-grid">
        <div class="calc-field">
          <label for="purchase-price">Purchase Price ($)</label>
          <input type="number" id="purchase-price" placeholder="500000" value="${propertyData.assessed_total || ''}">
        </div>
        <div class="calc-field">
          <label for="monthly-rent">Monthly Rent ($)</label>
          <input type="number" id="monthly-rent" placeholder="3500">
        </div>
        <div class="calc-field">
          <label for="down-payment">Down Payment (%)</label>
          <input type="number" id="down-payment" placeholder="25" value="25">
        </div>
        <div class="calc-field">
          <label for="interest-rate">Interest Rate (%)</label>
          <input type="number" id="interest-rate" step="0.1" placeholder="6.5" value="6.5">
        </div>
        <div class="calc-field">
          <label for="monthly-expenses">Monthly Expenses ($)</label>
          <input type="number" id="monthly-expenses" placeholder="500" value="500">
        </div>
      </div>
    </div>
    
    <div id="investment-results" style="display: none;"></div>
  `;
}

window.calculateInvestment = async function() {
  const btn = document.getElementById('calc-investment-btn');
  const resultsDiv = document.getElementById('investment-results');
  
  btn.disabled = true;
  btn.textContent = 'Calculating...';

  try {
    const params = {
      bbl: propertyData.bbl,
      purchase_price: document.getElementById('purchase-price').value,
      monthly_rent: document.getElementById('monthly-rent').value,
      down_payment_percent: document.getElementById('down-payment').value,
      interest_rate: document.getElementById('interest-rate').value,
      monthly_expenses: document.getElementById('monthly-expenses').value
    };

    const data = await apiPost('/api/investment-analyzer', params);
    const analysis = data.analysis;

    // Display results
    resultsDiv.style.display = 'block';
    resultsDiv.innerHTML = `
      <div class="investment-results">
        <div class="grade-badge grade-${analysis.investment_grade.toLowerCase()}">
          Grade ${analysis.investment_grade}
        </div>
        
        <div class="metrics-grid">
          <div class="metric-card">
            <div class="metric-value">$${formatNumber(analysis.net_monthly_cash_flow)}</div>
            <div class="metric-label">Monthly Cash Flow</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${analysis.cash_on_cash_return}%</div>
            <div class="metric-label">Cash-on-Cash Return</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${analysis.cap_rate}%</div>
            <div class="metric-label">Cap Rate</div>
          </div>
          <div class="metric-card">
            <div class="metric-value">${analysis.gross_rental_yield}%</div>
            <div class="metric-label">Gross Rental Yield</div>
          </div>
        </div>

        <div class="analysis-details">
          <div class="detail-row">
            <span>1% Rule (${analysis.one_percent_rule_ratio}%)</span>
            <span class="${analysis.meets_one_percent ? 'pass' : 'fail'}">${analysis.meets_one_percent ? '✓ Pass' : '✗ Fail'}</span>
          </div>
          <div class="detail-row">
            <span>2% Rule</span>
            <span class="${analysis.meets_two_percent ? 'pass' : 'fail'}">${analysis.meets_two_percent ? '✓ Pass' : '✗ Fail'}</span>
          </div>
          <div class="detail-row">
            <span>Monthly Mortgage Payment</span>
            <span>$${formatNumber(analysis.monthly_mortgage_payment)}</span>
          </div>
          <div class="detail-row">
            <span>Initial Cash Investment</span>
            <span>$${formatNumber(analysis.initial_cash_invested)}</span>
          </div>
        </div>
      </div>
    `;
  } catch (error) {
    resultsDiv.innerHTML = `<div class="error">Error calculating investment analysis: ${error.error || error.message}</div>`;
  } finally {
    btn.disabled = false;
    btn.textContent = 'Calculate';
  }
};

// ─── NEW: Neighborhood Market Trends ───

// ─── Monthly Price Trend Chart (pure SVG) ───

function buildPriceTrendChart(sales) {
  if (!sales || sales.length < 3) return '';

  // Group sales by month
  const monthMap = {};
  const now = new Date();
  // Build 12-month skeleton so we show gaps
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    monthMap[key] = { prices: [], label: d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) };
  }

  for (const s of sales) {
    if (!s.date || !s.price) continue;
    const key = s.date.slice(0, 7); // "YYYY-MM"
    if (monthMap[key]) monthMap[key].prices.push(s.price);
  }

  const months = Object.keys(monthMap).sort();
  const data = months.map(k => {
    const prices = monthMap[k].prices;
    const sorted = [...prices].sort((a, b) => a - b);
    const median = sorted.length ? sorted[Math.floor(sorted.length / 2)] : null;
    return { key: k, label: monthMap[k].label, median, count: prices.length };
  });

  // Need at least 2 months with data for a meaningful chart
  const withData = data.filter(d => d.median !== null);
  if (withData.length < 2) return '';

  // Chart dimensions
  const W = 720, H = 260, padL = 70, padR = 20, padT = 30, padB = 50;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  // Price scale (for line)
  const medians = withData.map(d => d.median);
  const minP = Math.min(...medians) * 0.9;
  const maxP = Math.max(...medians) * 1.1;
  const rangeP = maxP - minP || 1;

  // Volume scale (for bars)
  const maxCount = Math.max(...data.map(d => d.count), 1);

  // Build SVG
  const barW = Math.max(12, Math.floor(chartW / data.length) - 6);

  // Grid lines (4 horizontal)
  let gridLines = '';
  for (let i = 0; i <= 4; i++) {
    const y = padT + (chartH / 4) * i;
    const val = maxP - (rangeP / 4) * i;
    const label = val >= 1000000 ? `$${(val / 1000000).toFixed(1)}M` : val >= 1000 ? `$${Math.round(val / 1000)}K` : `$${Math.round(val)}`;
    gridLines += `<line x1="${padL}" y1="${y}" x2="${W - padR}" y2="${y}" stroke="var(--border)" stroke-dasharray="3,3" opacity="0.5"/>`;
    gridLines += `<text x="${padL - 8}" y="${y + 4}" text-anchor="end" fill="var(--text-muted)" font-size="11">${label}</text>`;
  }

  // Volume bars
  let bars = '';
  data.forEach((d, i) => {
    const x = padL + (i + 0.5) * (chartW / data.length);
    const barH = d.count > 0 ? Math.max(3, (d.count / maxCount) * (chartH * 0.35)) : 0;
    if (barH > 0) {
      bars += `<rect x="${x - barW / 2}" y="${padT + chartH - barH}" width="${barW}" height="${barH}" rx="2" fill="var(--primary)" opacity="0.2"/>`;
      bars += `<text x="${x}" y="${padT + chartH - barH - 4}" text-anchor="middle" fill="var(--text-muted)" font-size="10">${d.count}</text>`;
    }
  });

  // Price line (only connect months with data)
  let points = [];
  data.forEach((d, i) => {
    if (d.median === null) return;
    const x = padL + (i + 0.5) * (chartW / data.length);
    const y = padT + chartH - ((d.median - minP) / rangeP) * chartH;
    points.push({ x, y, median: d.median, label: d.label });
  });

  let pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');

  // Gradient fill under line
  let areaD = '';
  if (points.length >= 2) {
    areaD = pathD + ` L ${points[points.length - 1].x.toFixed(1)} ${padT + chartH} L ${points[0].x.toFixed(1)} ${padT + chartH} Z`;
  }

  // Dots and tooltips
  let dots = '';
  points.forEach(p => {
    dots += `<circle cx="${p.x.toFixed(1)}" cy="${p.y.toFixed(1)}" r="4" fill="var(--primary)" stroke="#fff" stroke-width="2"/>`;
  });

  // X-axis labels
  let xLabels = '';
  data.forEach((d, i) => {
    const x = padL + (i + 0.5) * (chartW / data.length);
    xLabels += `<text x="${x}" y="${H - 8}" text-anchor="middle" fill="var(--text-muted)" font-size="10" transform="rotate(-30 ${x} ${H - 8})">${d.label}</text>`;
  });

  // Trend arrow
  let trendBadge = '';
  if (points.length >= 2) {
    const first = points[0].median;
    const last = points[points.length - 1].median;
    const pctChange = ((last - first) / first * 100).toFixed(1);
    const isUp = last >= first;
    const arrow = isUp ? '&#9650;' : '&#9660;';
    const color = isUp ? '#22c55e' : '#ef4444';
    trendBadge = `<span style="display:inline-block;padding:4px 10px;border-radius:var(--radius);background:${color}15;color:${color};font-weight:600;font-size:13px;margin-left:12px">${arrow} ${isUp ? '+' : ''}${pctChange}%</span>`;
  }

  return `
    <div style="margin:20px 0">
      <h3 class="section-header">Price Trend ${trendBadge}</h3>
      <div style="overflow-x:auto">
        <svg viewBox="0 0 ${W} ${H}" width="100%" style="max-width:${W}px;font-family:inherit">
          ${gridLines}
          ${bars}
          ${areaD ? `<path d="${areaD}" fill="var(--primary)" opacity="0.08"/>` : ''}
          <path d="${pathD}" fill="none" stroke="var(--primary)" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          ${dots}
          ${xLabels}
          <text x="${padL}" y="${padT - 10}" fill="var(--text-muted)" font-size="11">Median Price</text>
          <text x="${W - padR}" y="${padT - 10}" text-anchor="end" fill="var(--text-muted)" font-size="11">Sales Volume (bars)</text>
        </svg>
      </div>
    </div>`;
}

let marketTrendsLoaded = false;

async function loadMarketTrends() {
  if (marketTrendsLoaded) return;
  marketTrendsLoaded = true;

  const container = document.getElementById('market-trends');
  if (!container) return;

  const isNYS = propertyData?.region === 'nys';
  if (isNYS) {
    container.innerHTML = '<p>Market trends with 3D sales map is available for NYC properties.</p>';
    return;
  }

  if (userTier === 'free') {
    container.innerHTML = `
      <div class="pro-lock">
        <h3>3D Market Trends</h3>
        <p>See a 3D map of recent property sales within 0.5 miles, with buildings extruded by sale price. Includes median price, price per sqft, and sales volume.</p>
        <a href="/pricing" class="btn btn-primary">Upgrade to Pro</a>
      </div>`;
    return;
  }

  // Show loading spinner
  container.innerHTML = `
    <div style="text-align:center;padding:40px">
      <div style="width:32px;height:32px;border:4px solid var(--border);border-top-color:var(--primary);border-radius:50%;animation:spin 0.8s linear infinite;margin:0 auto 12px"></div>
      <p style="color:var(--text-muted)">Loading market data and building 3D map...</p>
    </div>`;

  try {
    const data = await apiGet(`/api/market-trends?bbl=${propertyData.bbl}`);
    const { summary, sales, center, subject } = data;

    if (!sales || !sales.length) {
      container.innerHTML = '<p>No recent sales found within 0.5 miles of this property.</p>';
      return;
    }

    // Summary stats
    let html = '<h3 class="section-header">Market Trends (0.5 mi radius, last 12 months)</h3>';
    html += '<div class="stat-row">';
    html += `<div class="stat-card"><div class="stat-value">${summary.totalSales}</div><div class="stat-label">Sales</div></div>`;
    if (summary.medianPrice) html += `<div class="stat-card"><div class="stat-value">${formatCurrency(summary.medianPrice)}</div><div class="stat-label">Median Price</div></div>`;
    if (summary.medianPricePerSqft) html += `<div class="stat-card"><div class="stat-value">${formatCurrency(summary.medianPricePerSqft)}</div><div class="stat-label">Median $/sqft</div></div>`;
    if (summary.priceRange) html += `<div class="stat-card"><div class="stat-value">${formatCurrency(summary.priceRange.min)} - ${formatCurrency(summary.priceRange.max)}</div><div class="stat-label">Price Range</div></div>`;
    html += '</div>';

    // Monthly Price Trend Chart (SVG)
    html += buildPriceTrendChart(sales);

    // 3D Map container
    html += '<div id="market-map" style="width:100%;height:450px;border-radius:var(--radius);border:1px solid var(--border);margin:16px 0"></div>';

    // Sales table
    html += '<h3 class="section-header">Recent Sales</h3>';
    html += tableHtml(
      ['Address', 'Date', 'Price', '$/Sqft', 'Type'],
      sales.slice(0, 30).map(s => [
        s.address || '-',
        formatDate(s.date),
        formatCurrency(s.price),
        s.pricePerSqft ? formatCurrency(s.pricePerSqft) : '-',
        (s.category || '-').replace(/^\d+\s*/, ''),
      ]),
      { col: 2, dir: 'desc' }
    );

    container.innerHTML = html;

    // Initialize 3D map
    setTimeout(() => initMarketMap(center, subject, sales, summary), 100);
  } catch (error) {
    container.innerHTML = `<p>Could not load market trends: ${error.error || error.message || 'Unknown error'}</p>`;
  }
}

function initMarketMap(center, subject, sales, summary) {
  const mapContainer = document.getElementById('market-map');
  if (!mapContainer || typeof maplibregl === 'undefined') return;

  const map = new maplibregl.Map({
    container: 'market-map',
    style: 'https://tiles.openfreemap.org/styles/liberty',
    center: [center.lng, center.lat],
    zoom: 15,
    pitch: 50,
    bearing: -17,
    antialias: true,
  });
  map.addControl(new maplibregl.NavigationControl(), 'top-right');

  map.on('load', () => {
    // Color scale: green (low) → yellow (mid) → red (high)
    const maxPrice = Math.max(...sales.map(s => s.price));
    const minPrice = Math.min(...sales.map(s => s.price));

    // Build GeoJSON features for sales
    const features = sales.map(s => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      properties: {
        price: s.price,
        address: s.address,
        date: s.date,
        pricePerSqft: s.pricePerSqft || 0,
        category: s.category || '',
        height: Math.max(20, Math.round((s.price / maxPrice) * 200)),
        color: priceToColor(s.price, minPrice, maxPrice),
      },
    }));

    // Add sales as 3D extruded circles
    map.addSource('sales', {
      type: 'geojson',
      data: { type: 'FeatureCollection', features },
    });

    map.addLayer({
      id: 'sales-3d',
      type: 'circle',
      source: 'sales',
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['get', 'price'], minPrice, 8, maxPrice, 20],
        'circle-color': ['get', 'color'],
        'circle-opacity': 0.8,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff',
      },
    });

    // Subject property marker (blue star)
    new maplibregl.Marker({ color: '#1a56db' })
      .setLngLat([subject.lng, subject.lat])
      .setPopup(new maplibregl.Popup({ offset: 25 }).setHTML(
        `<strong>${subject.address || 'Subject Property'}</strong><br>Your property`
      ))
      .addTo(map)
      .togglePopup();

    // Click handler for sales
    map.on('click', 'sales-3d', (e) => {
      const props = e.features[0].properties;
      new maplibregl.Popup({ offset: 10 })
        .setLngLat(e.lngLat)
        .setHTML(`
          <strong>${props.address}</strong><br>
          Price: $${Number(props.price).toLocaleString()}<br>
          Date: ${props.date}<br>
          ${props.pricePerSqft > 0 ? `$/sqft: $${Number(props.pricePerSqft).toLocaleString()}<br>` : ''}
          ${props.category ? props.category.replace(/^\d+\s*/, '') : ''}
        `)
        .addTo(map);
    });

    map.on('mouseenter', 'sales-3d', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'sales-3d', () => { map.getCanvas().style.cursor = ''; });

    // Add radius circle
    const circleCoords = createCircleCoords(center.lng, center.lat, 805); // 0.5 miles in meters
    map.addSource('radius', {
      type: 'geojson',
      data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [circleCoords] } },
    });
    map.addLayer({
      id: 'radius-fill', type: 'fill', source: 'radius',
      paint: { 'fill-color': '#1a56db', 'fill-opacity': 0.05 },
    });
    map.addLayer({
      id: 'radius-line', type: 'line', source: 'radius',
      paint: { 'line-color': '#1a56db', 'line-width': 2, 'line-dasharray': [3, 3] },
    });
  });
}

function priceToColor(price, min, max) {
  const ratio = max > min ? (price - min) / (max - min) : 0.5;
  // Green → Yellow → Red
  const r = Math.round(ratio < 0.5 ? ratio * 2 * 255 : 255);
  const g = Math.round(ratio < 0.5 ? 255 : (1 - (ratio - 0.5) * 2) * 255);
  return `rgb(${r},${g},50)`;
}

function createCircleCoords(lng, lat, radiusMeters) {
  const coords = [];
  const km = radiusMeters / 1000;
  for (let i = 0; i <= 64; i++) {
    const angle = (i / 64) * 2 * Math.PI;
    const dlat = (km / 111.32) * Math.cos(angle);
    const dlng = (km / (111.32 * Math.cos(lat * Math.PI / 180))) * Math.sin(angle);
    coords.push([lng + dlng, lat + dlat]);
  }
  return coords;
}

// ─── NEW: Comparable Sales ───

let comparablesLoaded = false;

async function loadComparables() {
  if (comparablesLoaded || userTier === 'free') return;
  comparablesLoaded = true;

  const container = document.getElementById('comparable-sales');
  if (!container) return;

  if (userTier !== 'pro' && userTier !== 'enterprise') {
    container.innerHTML = `
      <div class="pro-feature">
        <h3>🏠 Comparable Sales</h3>
        <p>View recent sales of similar properties in the area with valuation estimates.</p>
        <a href="/pricing" class="btn-primary">Upgrade to Pro</a>
      </div>
    `;
    return;
  }

  try {
    const data = await apiGet(`/api/comparables?bbl=${propertyData.bbl}&radius=0.5&months=12`);
    const comps = data.comparables;

    if (comps.error) {
      container.innerHTML = `<div class="error">Could not load comparables: ${comps.error}</div>`;
      return;
    }

    container.innerHTML = `
      <h3>🏠 Comparable Sales</h3>
      
      ${comps.market_statistics?.estimated_value ? `
        <div class="valuation-estimate">
          <h4>Estimated Market Value</h4>
          <div class="value-range">
            <div class="value-item">
              <strong>Range:</strong> ${formatCurrency(comps.market_statistics.estimated_value.low)} - ${formatCurrency(comps.market_statistics.estimated_value.high)}
            </div>
            <div class="value-item">
              <strong>Median:</strong> ${formatCurrency(comps.market_statistics.estimated_value.median)}
            </div>
          </div>
        </div>
      ` : ''}

      <div class="comparables-list">
        ${comps.comparable_sales?.slice(0, 10).map(comp => `
          <div class="comparable-card">
            <div class="comp-header">
              <div class="comp-address">${comp.address || 'Address not available'}</div>
              <div class="comp-price">${formatCurrency(comp.sale_price)}</div>
            </div>
            <div class="comp-details">
              <div class="comp-meta">
                ${comp.bldg_area ? `${formatNumber(comp.bldg_area)} sq ft` : ''} ${comp.bldg_area && comp.units_total ? '•' : ''} 
                ${comp.units_total ? `${comp.units_total} units` : ''} ${(comp.bldg_area || comp.units_total) && comp.year_built ? '•' : ''}
                ${comp.year_built ? `Built ${comp.year_built}` : ''}
              </div>
              <div class="comp-stats">
                <span class="comp-stat">Sold ${Math.round(comp.days_since_sale)} days ago</span>
                ${comp.price_per_sqft ? `<span class="comp-stat">$${comp.price_per_sqft}/sq ft</span>` : ''}
                <span class="comp-stat similarity-score">Match: ${comp.similarity_score}%</span>
              </div>
            </div>
          </div>
        `).join('')}
      </div>

      ${comps.comparable_sales?.length === 0 ? `
        <div class="no-data">No recent comparable sales found in this area.</div>
      ` : ''}
    `;
  } catch (error) {
    container.innerHTML = `<div class="error">Error loading comparable sales: ${error.error || error.message}</div>`;
  }
}

// ─── Mortgage Estimator (overview tab, all tiers, client-side only) ───
// calcMonthlyMortgage + estimatePriceFromProperty imported from ./mortgage-utils.js

function renderMortgageCalc(p) {
  const container = document.getElementById('overview-mortgage-calc');
  if (!container) return;

  const estimated = estimatePriceFromProperty(p);
  const defaultPrice = estimated ? Math.round(estimated / 1000) * 1000 : '';

  container.innerHTML = `
    <div class="mortgage-calc-wrap">
      <div class="mortgage-calc-header">
        <span class="mortgage-calc-title">Mortgage Estimator</span>
        <span class="mortgage-calc-badge">Free</span>
      </div>
      <div class="mortgage-calc-grid">
        <div class="mc-field">
          <label>Purchase Price ($)</label>
          <input type="number" id="mc-price" min="1000" step="1000"
            value="${defaultPrice}" placeholder="e.g. 750000">
        </div>
        <div class="mc-field">
          <label>Down Payment (%)</label>
          <input type="number" id="mc-down" min="0" max="100" step="1" value="20" placeholder="20">
        </div>
        <div class="mc-field">
          <label>Interest Rate (%)</label>
          <input type="number" id="mc-rate" min="0.1" max="30" step="0.1" value="7.0" placeholder="7.0">
        </div>
        <div class="mc-field">
          <label>Loan Term (years)</label>
          <input type="number" id="mc-years" min="1" max="40" step="1" value="30" placeholder="30">
        </div>
      </div>
      <div id="mc-results"></div>
    </div>
  `;

  addMortgageStyles();
  updateMortgageResults();

  ['mc-price', 'mc-down', 'mc-rate', 'mc-years'].forEach(id => {
    document.getElementById(id)?.addEventListener('input', updateMortgageResults);
  });
}

function updateMortgageResults() {
  const price = parseFloat(document.getElementById('mc-price')?.value);
  const down = parseFloat(document.getElementById('mc-down')?.value) || 20;
  const rate = parseFloat(document.getElementById('mc-rate')?.value) || 7;
  const years = parseInt(document.getElementById('mc-years')?.value, 10) || 30;
  const out = document.getElementById('mc-results');
  if (!out) return;

  if (!price || price <= 0) {
    out.innerHTML = '<p class="mc-hint">Enter a purchase price to see the estimate.</p>';
    return;
  }

  const monthly = calcMonthlyMortgage(price, down, rate, years);
  const principal = price * (1 - down / 100);
  const totalPaid = monthly * years * 12;
  const totalInterest = totalPaid - principal;
  const downAmount = price * (down / 100);

  out.innerHTML = `
    <div class="mc-result-grid">
      <div class="mc-result-card mc-primary">
        <div class="mc-result-label">Monthly P&amp;I</div>
        <div class="mc-result-value">$${Math.round(monthly).toLocaleString()}</div>
      </div>
      <div class="mc-result-card">
        <div class="mc-result-label">Down Payment</div>
        <div class="mc-result-value">$${Math.round(downAmount).toLocaleString()}</div>
      </div>
      <div class="mc-result-card">
        <div class="mc-result-label">Loan Amount</div>
        <div class="mc-result-value">$${Math.round(principal).toLocaleString()}</div>
      </div>
      <div class="mc-result-card">
        <div class="mc-result-label">Total Interest</div>
        <div class="mc-result-value">$${Math.round(totalInterest).toLocaleString()}</div>
      </div>
    </div>
    <p class="mc-disclaimer">Principal &amp; interest only. Does not include taxes, insurance, or HOA.</p>
  `;
}

function addMortgageStyles() {
  if (document.getElementById('mc-styles')) return;
  const s = document.createElement('style');
  s.id = 'mc-styles';
  s.textContent = `
    .mortgage-calc-wrap { margin-top: 1.5rem; background: var(--bg-card, #f8fafc);
      border: 1px solid var(--border, #e2e8f0); border-radius: 12px; padding: 1.25rem; }
    .mortgage-calc-header { display: flex; align-items: center; gap: .6rem; margin-bottom: 1rem; }
    .mortgage-calc-title { font-size: .95rem; font-weight: 700; color: var(--text, #111); }
    .mortgage-calc-badge { font-size: .62rem; font-weight: 700; padding: .15rem .45rem;
      border-radius: 20px; background: rgba(16,185,129,.12); color: #10b981;
      border: 1px solid rgba(16,185,129,.3); text-transform: uppercase; letter-spacing: .5px; }
    .mortgage-calc-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: .75rem; margin-bottom: 1rem; }
    @media (min-width: 640px) { .mortgage-calc-grid { grid-template-columns: repeat(4, 1fr); } }
    .mc-field label { font-size: .75rem; font-weight: 600; display: block; margin-bottom: .3rem;
      color: var(--text-muted, #6b7280); }
    .mc-field input { width: 100%; background: var(--bg, #fff); border: 1px solid var(--border, #e2e8f0);
      border-radius: 8px; padding: .5rem .7rem; font-size: .9rem; color: var(--text, #111);
      box-sizing: border-box; outline: none; transition: border-color .2s; }
    .mc-field input:focus { border-color: var(--primary, #2563eb); }
    .mc-result-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: .6rem; }
    @media (min-width: 640px) { .mc-result-grid { grid-template-columns: repeat(4, 1fr); } }
    .mc-result-card { background: var(--bg, #fff); border: 1px solid var(--border, #e2e8f0);
      border-radius: 8px; padding: .65rem .75rem; text-align: center; }
    .mc-result-card.mc-primary { border-color: var(--primary, #2563eb); background: rgba(37,99,235,.05); }
    .mc-result-label { font-size: .68rem; color: var(--text-muted, #6b7280);
      text-transform: uppercase; letter-spacing: .4px; margin-bottom: .3rem; }
    .mc-result-value { font-size: 1.05rem; font-weight: 800; color: var(--text, #111); }
    .mc-primary .mc-result-value { color: var(--primary, #2563eb); }
    .mc-hint { font-size: .8rem; color: var(--text-muted, #6b7280); padding: .5rem 0; }
    .mc-disclaimer { font-size: .72rem; color: var(--text-muted, #6b7280);
      margin-top: .75rem; padding-top: .5rem; border-top: 1px solid var(--border, #e2e8f0); }
  `;
  document.head.appendChild(s);
}
